import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { uprightJpegBytes } from '../utils/imageOrient';
import {
  Wrench, Plus, X, Save, Search, AlertTriangle, Truck, Clock,
  CalendarClock, Trash2, Pencil, Loader, History, DollarSign,
  ChevronDown, ChevronUp, RefreshCw, FileText, Paperclip, ExternalLink, CheckCircle, Download, Camera, Eye,
  ClipboardCheck, Bell, BellRing,
} from 'lucide-react';
import { watchStanding, watchTarget, DEFAULT_LEAD_KM } from '../utils/vehicleWatch';

// Vehicle service now runs on Supabase (vehicle-service-sb); was the appsscript proxy.
const VEHICLE_SERVICE_URL = '/.netlify/functions/vehicle-service-sb';
const SHIFT_ODO_URL = '/.netlify/functions/shift-odo-sb';
// Morning vehicle inspection register (vehicle-inspection-sb) — the Inspections tab.
const INSPECTION_URL = '/.netlify/functions/vehicle-inspection-sb';
// Maintenance reminders ("brakes in ~5,000–10,000 km") — vehicle-watch-sb.
const WATCH_URL = '/.netlify/functions/vehicle-watch-sb';
const INSP_FLAB = { display: 'block', fontSize: '0.62rem', color: 'var(--hp-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const DEFAULT_PROVIDER = 'Chatswood LDV Service Centre';

const JOB_TYPES = [
  'Scheduled Service', 'Repair', 'Tyres', 'Brakes', 'Battery', 'Windscreen',
  'Accident / Insurance', 'Pink Slip / Inspection', 'Breakdown', 'Other',
];
const STATUSES = ['Booked', 'In Workshop', 'Completed'];

const TYPE_COLORS = {
  'Scheduled Service':      'var(--hp-green)',
  'Repair':                 'var(--hp-amber)',
  'Tyres':                  'var(--hp-blue)',
  'Brakes':                 'var(--hp-blue)',
  'Battery':                'var(--hp-blue)',
  'Windscreen':             'var(--hp-blue)',
  'Accident / Insurance':   'var(--hp-red)',
  'Pink Slip / Inspection': 'var(--hp-gold)',
  'Breakdown':              'var(--hp-red)',
  'Other':                  'var(--hp-dim)',
};

// ── Date / money helpers ──────────────────────────────────────────────────────

// Local-date ISO (toISOString is UTC and shifts Sydney dates back a day/month)
const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const todayISO = () => toLocalISO(new Date());

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((d - new Date()) / 86400000);
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });

const fmtKm = (n) => (Number(n) || 0).toLocaleString('en-AU') + ' km';

function addMonthsISO(iso, months) {
  const src = iso || todayISO();
  const [y, m, day] = src.split('-').map(Number);
  if (!y || !m || !day) return '';
  const d = new Date(y, m - 1 + months, day);
  return toLocalISO(d);
}

// Australian financial year (1 July – 30 June)
function fyStartISO() {
  const now = new Date();
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-07-01`;
}
function fyLabel() {
  const start = Number(fyStartISO().slice(0, 4));
  return `FY${String(start).slice(2)}-${String(start + 1).slice(2)}`;
}

// Best date to file a record under: workshop date first, then booking, then created.
const recordDate = (r) => r.dateIn || r.bookedDate || r.dateOut || (r.createdAt || '').slice(0, 10);

// Drive direct-download URL for an attached invoice (falls back to parsing the
// file id out of the view URL for records saved before the id column existed).
function pdfDownloadUrl(r) {
  const id = r.invoiceFileId || (r.invoicePdfUrl || '').match(/\/file\/d\/([^/]+)/)?.[1];
  return id ? `https://drive.google.com/uc?export=download&id=${id}` : '';
}

const isOpen = (r) => r.status === 'Booked' || r.status === 'In Workshop';

const DEFAULT_SERVICE_INTERVAL_KM = 15000;

// The odometer at which a vehicle's next scheduled service falls due. Derived from
// the LATEST completed Scheduled Service — never a max/aggregate across history, so a
// stale milestone on an old service (or a next-km set by a Repair row) can't win.
// An explicitly recorded next-km is honoured only when it sits AFTER that service's
// own odometer; otherwise it's a mis-entered absolute (e.g. the bare interval "15,000"
// stored as the target) and we fall back to lastServiceOdo + interval.
function deriveNextServiceKm(records, rego, intervalKm) {
  const interval = intervalKm > 0 ? intervalKm : DEFAULT_SERVICE_INTERVAL_KM;
  const scheduled = (records || [])
    .filter(r => r.rego === rego && r.type === 'Scheduled Service' && r.status === 'Completed' && (Number(r.odometer) || 0) > 0)
    .sort((a, b) => (recordDate(b) || '').localeCompare(recordDate(a) || ''));
  const last = scheduled[0];
  if (!last) return 0;
  const base = Number(last.odometer) || 0;
  const stored = Number(last.nextServiceKm) || 0;
  return stored > base ? stored : base + interval;
}

// Live days off road for a vehicle still in the workshop.
function liveDowntime(r) {
  if (r.status !== 'In Workshop' || !r.dateIn) return 0;
  return Math.max(1, Math.floor((new Date() - new Date(r.dateIn)) / 86400000) + 1);
}

// ── Small UI bits ─────────────────────────────────────────────────────────────

function Pill({ bg, color, children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 20,
      fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--font-mono)',
      textTransform: 'uppercase', letterSpacing: '0.05em', background: bg, color,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function StatusPill({ status }) {
  if (status === 'In Workshop') return <Pill bg="var(--hp-red)" color="#fff">In Workshop</Pill>;
  if (status === 'Booked')      return <Pill bg="var(--hp-gold)" color="#000">Booked</Pill>;
  if (status === 'Completed')   return <Pill bg="rgba(0,0,0,0)" color="var(--hp-green)">✓ Completed</Pill>;
  return <Pill bg="var(--hp-surface)" color="var(--hp-muted)">{status || '—'}</Pill>;
}

function DuePill({ days, hasDate, kmLeft }) {
  // Kilometres can trip the pill red/amber even when the calendar date looks fine —
  // whichever measure (date or odometer) is more urgent wins.
  if (days !== null && hasDate && days < 0) return <Pill bg="var(--hp-red)" color="#fff">{`Overdue ${Math.abs(days)}d`}</Pill>;
  if (kmLeft != null && kmLeft < 0) return <Pill bg="var(--hp-red)" color="#fff">{`${fmtKm(Math.abs(kmLeft))} over`}</Pill>;
  if (days !== null && hasDate && days === 0) return <Pill bg="var(--hp-red)" color="#fff">Due Today</Pill>;
  if (kmLeft != null && kmLeft <= 500) return <Pill bg="var(--hp-amber)" color="#000">{`${fmtKm(kmLeft)} left`}</Pill>;
  if (!hasDate || days === null) return <Pill bg="var(--hp-surface)" color="var(--hp-muted)">No Date</Pill>;
  if (days <= 14) return <Pill bg="var(--hp-amber)" color="#000">{`${days}d left`}</Pill>;
  if (days <= 30) return <Pill bg="var(--hp-gold)" color="#000">{`${days}d left`}</Pill>;
  return <Pill bg="rgba(0,0,0,0)" color="var(--hp-green)">{`✓ ${days}d`}</Pill>;
}

function TypeBadge({ type }) {
  const color = TYPE_COLORS[type] || 'var(--hp-dim)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--hp-text)', whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {type || '—'}
    </span>
  );
}

const labelStyle = {
  fontSize: '.82rem', color: 'var(--hp-text)', fontWeight: 600,
  letterSpacing: '.01em', display: 'block', marginBottom: 6,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%', fontSize: '.95rem', padding: '9px 11px' };

function SectionTitle({ n, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, marginTop: n > 1 ? 26 : 0, paddingBottom: 8, borderBottom: '1px solid var(--hp-border)' }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--hp-gold)', color: '#000', fontSize: '.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-gold)', fontWeight: 700 }}>{title}</span>
    </div>
  );
}

// ── Alert panel ───────────────────────────────────────────────────────────────

function ServiceAlertPanel({ summaries, watchAlerts = [], onLog, onBackOnRoad, onWatch }) {
  const [open, setOpen] = useState(true);

  const alerts = useMemo(() => {
    const items = [];
    // Reminders that have closed in (due soon / in the window / past it) sit in the same
    // list as service alerts — one place to look, ordered by urgency with everything else.
    watchAlerts.forEach(({ w, st }) => {
      items.push({
        rego: w.rego, kind: st.stage === 'over' ? 'OVERDUE' : st.stage === 'due' ? 'DUE NOW' : 'DUE SOON',
        detail: `${w.item} — ${watchTarget(w)}${st.label ? ` · ${st.label}` : ''}`,
        days: st.stage === 'over' ? -2 : st.stage === 'due' ? -0.5 : 7,
        accent: st.stage === 'over' ? 'var(--hp-red)' : 'var(--hp-amber)',
        watch: true, canBook: true,
      });
    });
    summaries.forEach(s => {
      if (s.openRecord?.status === 'In Workshop') {
        items.push({ rego: s.rego, kind: 'OFF ROAD', detail: `${s.openRecord.type || 'Job'} at ${s.openRecord.provider || 'workshop'} — day ${liveDowntime(s.openRecord)}`, days: -9999, accent: 'var(--hp-red)', openRecord: s.openRecord });
      } else if (!s.openRecord && ((s.dueDays !== null && s.dueDays <= 30) || (s.kmLeft != null && s.kmLeft <= 500))) {
        const overdue = (s.dueDays !== null && s.dueDays < 0) || (s.kmLeft != null && s.kmLeft < 0);
        const kmNote = s.kmLeft != null
          ? ` — ${s.kmLeft < 0 ? `${fmtKm(Math.abs(s.kmLeft))} over` : `${fmtKm(s.kmLeft)} to go`}`
          : '';
        items.push({
          rego: s.rego,
          kind: overdue ? 'OVERDUE' : 'DUE SOON',
          detail: `Service due ${s.nextDue ? fmtDate(s.nextDue) : '—'}${s.nextDueKm ? ` or ${fmtKm(s.nextDueKm)}` : ''}${kmNote}`,
          days: overdue ? Math.min(s.dueDays ?? -1, -1) : (s.dueDays ?? 30),
          accent: overdue ? 'var(--hp-red)' : (s.dueDays !== null && s.dueDays <= 14) || (s.kmLeft != null && s.kmLeft <= 500) ? 'var(--hp-amber)' : 'var(--hp-gold)',
          canBook: true,
        });
      }
    });
    return items.sort((a, b) => a.days - b.days);
  }, [summaries]);

  if (alerts.length === 0) return null;
  const critical = alerts.filter(a => a.days < 0).length;
  const accent = critical > 0 ? 'var(--hp-red)' : 'var(--hp-amber)';

  return (
    <div style={{ background: 'var(--hp-surface)', border: `1px solid ${accent}44`, borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px',
          background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
          borderBottom: open ? '1px solid var(--hp-border)22' : 'none',
        }}
      >
        <AlertTriangle size={15} color={accent} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {alerts.length} Maintenance Alert{alerts.length !== 1 ? 's' : ''}
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--hp-muted)', display: 'flex' }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {open && alerts.map((a, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '10px 18px',
          borderBottom: i < alerts.length - 1 ? '1px solid var(--hp-border)22' : 'none',
          boxShadow: `inset 4px 0 0 ${a.accent}`,
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)', minWidth: 72 }}>{a.rego}</span>
          <Pill bg={a.accent} color={a.accent === 'var(--hp-red)' ? '#fff' : '#000'}>{a.kind}</Pill>
          {a.watch && <BellRing size={13} color={a.accent} style={{ flexShrink: 0 }} />}
          <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', flex: 1 }}>{a.detail}</span>
          {a.watch && (
            <button className="btn btn-ghost btn-sm" title="Open this van's reminders" onClick={() => onWatch(a.rego)}>
              <Bell size={11} /> Reminder
            </button>
          )}
          {a.openRecord && (
            <button className="btn btn-ghost btn-sm" title="Close this job — the van drops off the alerts" onClick={() => onBackOnRoad(a.openRecord)}>
              <CheckCircle size={11} /> Back on road
            </button>
          )}
          {a.canBook && (
            <button className="btn btn-ghost btn-sm" onClick={() => onLog(a.rego, 'Booked')}>
              <CalendarClock size={11} /> Book
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Add / Edit drawer ─────────────────────────────────────────────────────────

const EMPTY_RECORD = {
  id: '', rego: '', type: 'Scheduled Service', status: 'Booked',
  bookedDate: '', dateIn: '', dateOut: '', odometer: '',
  provider: '', description: '', cost: '', invoiceNo: '',
  downtimeDays: '', nextServiceDate: '', nextServiceKm: '', notes: '',
  visibility: 'admin',
  invoicePdfUrl: '', invoiceFileId: '',
};

const MAX_PDF_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB
// What may actually go up the wire. The function body is JSON with the file base64'd
// inside it, and base64 inflates by ~33%, so 4.2 MB of PDF is ~5.6 MB of request —
// the most that clears Netlify's 6 MB limit. A multi-page camera scan blows past this
// long before any single page hits MAX_PDF_MB, which is how a 5-page invoice ends up
// saving the record and silently losing the attachment.
const MAX_UPLOAD_BYTES = Math.round(4.2 * 1024 * 1024);
// Re-encode ladder for scanned pages: start at the quality the reader wants, drop a
// rung at a time until the combined PDF fits. 2400/0.85 matches the old fixed setting,
// so a 1-2 page scan is byte-for-byte what it was before.
const SCAN_STEPS = [
  { maxEdge: 2400, quality: 0.85 },
  { maxEdge: 2000, quality: 0.75 },
  { maxEdge: 1600, quality: 0.65 },
  { maxEdge: 1200, quality: 0.55 },
];
const mb = (bytes) => (bytes / 1024 / 1024).toFixed(2);
const UPLOAD_TIMEOUT_MS = 120000; // a stalled phone upload should fail visibly, not spin
// A scanned page is re-encoded before it goes anywhere, so its raw size doesn't matter —
// a 48 MP iPhone capture is well over MAX_PDF_MB and used to be refused outright even
// though the very next step would have shrunk it. Cap it only to catch a stray video.
const MAX_SCAN_PAGE_MB = 25;

// Build the combined scan PDF at the highest quality that still fits the upload budget.
// Returns { file, overBudget, step } — overBudget means even the bottom rung is too big
// (the caller warns rather than letting the upload fail at the far end).
async function buildScanPdf(pages, onStep) {
  const { PDFDocument } = await import('pdf-lib');
  let best = null;
  for (let i = 0; i < SCAN_STEPS.length; i++) {
    const { maxEdge, quality } = SCAN_STEPS[i];
    if (i > 0 && onStep) onStep(`Too big — re-encoding ${pages.length} pages smaller…`);
    const pdf = await PDFDocument.create();
    let reEncodedAny = false;
    for (const img of pages) {
      const upright = await uprightJpegBytes(img, maxEdge, quality); // EXIF orientation + downscale
      let embedded;
      if (upright) {
        reEncodedAny = true;
        embedded = await pdf.embedJpg(upright);
      } else {
        const buf = await img.arrayBuffer();
        embedded = img.type === 'image/png' ? await pdf.embedPng(buf) : await pdf.embedJpg(buf);
      }
      const page = pdf.addPage([embedded.width, embedded.height]);
      page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });
    }
    const bytes = await pdf.save();
    best = new File([bytes], `invoice-scan-${pages.length}p.pdf`, { type: 'application/pdf' });
    if (bytes.byteLength <= MAX_UPLOAD_BYTES) return { file: best, overBudget: false, step: i };
    // Nothing was re-encoded (no createImageBitmap, or every call timed out), so the
    // next rung would produce the identical bytes — stop instead of looping for nothing.
    if (!reEncodedAny) break;
  }
  return { file: best, overBudget: true, step: SCAN_STEPS.length - 1 };
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.readAsDataURL(file);
});

// ── Invoice auto-extraction (Claude reads the dropped PDF) ────────────────────

const EXTRACT_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    rego:            { type: 'string', description: 'Vehicle registration / number plate, uppercase, no spaces. Empty string if not on the invoice.' },
    invoiceNo:       { type: 'string', description: 'Invoice number/reference. Empty string if not found.' },
    invoiceDate:     { type: 'string', description: 'Invoice or service date as YYYY-MM-DD. Empty string if not found.' },
    odometer:        { type: 'number', description: 'Odometer/kilometre reading at service. 0 if not shown.' },
    provider:        { type: 'string', description: 'The name of the workshop / service centre / business that ISSUED this invoice — read it from the letterhead, logo or "from" details at the top of the document, or the payee on the remittance. This is the trading name of the business being paid, NOT the vehicle make/model and NOT a default. Empty string if genuinely not shown.' },
    description:     { type: 'string', description: 'Short plain summary of the work performed, under 120 characters.' },
    jobType:         { type: 'string', enum: ['Scheduled Service', 'Repair', 'Tyres', 'Brakes', 'Battery', 'Windscreen', 'Accident / Insurance', 'Pink Slip / Inspection', 'Breakdown', 'Other'], description: 'Best-fit job category for the work on the invoice.' },
    totalExGst:      { type: 'number', description: 'Invoice total EXCLUDING GST in dollars. 0 if not determinable.' },
    totalIncGst:     { type: 'number', description: 'Invoice total INCLUDING GST in dollars. 0 if not determinable.' },
    nextServiceDate: { type: 'string', description: 'Next service due date as YYYY-MM-DD if printed on the invoice, else empty string.' },
    nextServiceKm:   { type: 'number', description: 'Next service due odometer reading in km if printed, else 0.' },
  },
  required: ['rego', 'invoiceNo', 'invoiceDate', 'odometer', 'provider', 'description', 'jobType', 'totalExGst', 'totalIncGst', 'nextServiceDate', 'nextServiceKm'],
};

async function extractInvoiceFields(file) {
  const keyResp = await fetch('/.netlify/functions/get-claude-key');
  const { key } = await keyResp.json();
  if (!key) throw new Error('Could not get API key');

  const data = await fileToBase64(file);
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  const docBlock = isPdf
    ? { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data } }
    : { type: 'image', source: { type: 'base64', media_type: file.type || 'image/jpeg', data } };

  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-opus-4-8',
      max_tokens: 1500,
      output_config: { format: { type: 'json_schema', schema: EXTRACT_SCHEMA } },
      messages: [{
        role: 'user',
        content: [
          docBlock,
          { type: 'text', text: `Today's date is ${todayISO()}. This is a vehicle service/repair tax invoice from an Australian workshop. Extract the fields. Read the issuing workshop's name from the actual letterhead/logo on THIS document — different jobs come from different service centres, so never assume a default or reuse a name that isn't printed here. Dates without a year belong to the current or most recent past year, never the future. Amounts are AUD.` },
        ],
      }],
    }),
  });
  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Claude error ${resp.status}: ${errText.substring(0, 150)}`);
  }
  const result = await resp.json();
  const raw = (result.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
  return JSON.parse(raw);
}

function RecordDrawer({ record, vehicles, personnel, providers, onClose, onSaved, addNotification }) {
  const [form, setForm] = useState({ ...EMPTY_RECORD, ...record });
  const [incGst, setIncGst] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [saveStep, setSaveStep] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedNote, setExtractedNote] = useState('');
  const [scanPages, setScanPages] = useState([]); // accumulated photo pages → combined into one PDF
  const [building, setBuilding] = useState(false); // combining/re-encoding the scan pages
  const [buildNote, setBuildNote] = useState('');  // size of the combined scan vs the upload budget
  const [attachFailed, setAttachFailed] = useState(false); // record saved, invoice upload didn't
  const fileInputRef = React.useRef(null);
  const ocrSeqRef = React.useRef(0); // guards overlapping reads: only the latest scan's result applies
  const isEdit = !!record.id;

  // Pure merge: applies extracted invoice fields onto the form, never
  // overwriting anything the user already typed. Returns the new form and
  // the list of field labels that were filled.
  const mergeExtraction = (prev, x) => {
    const next = { ...prev };
    const filled = [];
    const fill = (field, value, label) => {
      if (value === '' || value === 0 || value == null) return;
      const current = prev[field];
      if (current !== '' && current !== 0 && current != null) return;
      next[field] = value;
      filled.push(label || field);
    };

    // Vehicle: only auto-select when the invoice rego matches the fleet.
    const rego = (x.rego || '').toUpperCase().replace(/[\s-]/g, '');
    if (!prev.rego && rego && vehicles.some(v => v.rego === rego)) {
      next.rego = rego;
      filled.push(`vehicle ${rego}`);
    }

    fill('invoiceNo', x.invoiceNo, 'invoice no');
    fill('provider', x.provider, 'workshop');
    fill('description', x.description, 'description');
    fill('odometer', x.odometer, 'odometer');
    fill('nextServiceDate', x.nextServiceDate, 'next service date');
    fill('nextServiceKm', x.nextServiceKm, 'next service km');

    if (x.invoiceDate) {
      fill('dateIn', x.invoiceDate, 'date');
      if (!prev.dateOut) next.dateOut = x.invoiceDate;
      // An invoice means the work is done — flip the default Booked status.
      if (prev.status === 'Booked') {
        next.status = 'Completed';
        filled.push('status → Completed');
      }
    }

    const cost = x.totalExGst > 0 ? x.totalExGst
      : x.totalIncGst > 0 ? Math.round((x.totalIncGst / 1.1) * 100) / 100 : 0;
    fill('cost', cost ? String(cost) : '', 'cost');

    if (JOB_TYPES.includes(x.jobType) && prev.type === EMPTY_RECORD.type && x.jobType !== prev.type) {
      next.type = x.jobType;
      filled.push('job type');
    }

    return { next, filled };
  };

  // Reads the dropped invoice with Claude and fills any field the user hasn't
  // already typed in — never overwrites manual input.
  const autoFillFromInvoice = async (f) => {
    const seq = ++ocrSeqRef.current; // this read supersedes any earlier in-flight one
    setExtracting(true);
    setExtractedNote('');
    try {
      const x = await extractInvoiceFields(f);
      if (seq !== ocrSeqRef.current) return; // a newer scan started — drop this stale result
      let filled = [];
      setForm(prev => {
        const merged = mergeExtraction(prev, x);
        filled = merged.filled;
        return merged.next;
      });
      // flushed synchronously enough for the note on the next tick
      setTimeout(() => {
        setExtractedNote(filled.length
          ? `Auto-filled from invoice: ${filled.join(', ')} — check before saving`
          : 'Invoice read — all fields already had values, nothing changed');
      }, 0);
      const regoOnInvoice = (x.rego || '').toUpperCase().replace(/[\s-]/g, '');
      if (regoOnInvoice && !vehicles.some(v => v.rego === regoOnInvoice)) {
        addNotification(`Invoice is for ${regoOnInvoice}, which isn't in the fleet — select the vehicle manually`, 'error');
      }
    } catch (err) {
      if (seq !== ocrSeqRef.current) return;
      setExtractedNote('');
      addNotification(`Couldn't read the invoice automatically (${err.message}) — fill the fields manually, the PDF will still attach on save`, 'error');
    } finally {
      if (seq === ocrSeqRef.current) setExtracting(false);
    }
  };

  const acceptFile = (f) => {
    if (!f) return;
    const okType = f.type === 'application/pdf' || f.type.startsWith('image/') || /\.pdf$/i.test(f.name);
    if (!okType) {
      addNotification('Only PDF or image files can be attached', 'error');
      return;
    }
    if (f.size > MAX_PDF_MB * 1024 * 1024) {
      addNotification(`File too large — max ${MAX_PDF_MB} MB`, 'error');
      return;
    }
    setScanPages([]);          // a dropped / browsed file replaces any scanned pages
    setBuildNote('');
    setPdfFile(f);
    autoFillFromInvoice(f);
  };

  const handleFilePick = (e) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = '';
  };

  // Multi-page scanning: each camera capture appends a page; all pages are combined into a
  // single multi-page PDF (pdf-lib, loaded lazily) that stores as one invoice on save.
  const addScanPage = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { addNotification('That wasn’t a photo — use the camera', 'error'); return; }
    if (f.size > MAX_SCAN_PAGE_MB * 1024 * 1024) { addNotification(`Page too large — max ${MAX_SCAN_PAGE_MB} MB`, 'error'); return; }
    const pages = [...scanPages, f];
    setScanPages(pages);
    setBuilding(true);
    setBuildNote('');
    try {
      // Fits the combined PDF to the upload budget before it ever reaches Save — a
      // 5-page scan at full quality is ~6 MB, which the function body can't carry.
      const { file: combined, overBudget } = await buildScanPdf(pages, setBuildNote);
      setPdfFile(combined);
      setBuildNote(
        overBudget
          ? `${pages.length} pages is ${mb(combined.size)} MB — over the ${mb(MAX_UPLOAD_BYTES)} MB upload limit. Remove a page, or scan the invoice in two records.`
          : `${pages.length} page${pages.length > 1 ? 's' : ''} · ${mb(combined.size)} MB — fits`);
      // Don't OCR yet — the user reads the whole multi-page PDF once, when finished scanning,
      // via the "Read all pages" button below. That way every page is read, not just page 1.
      setExtractedNote('');
    } catch (e) {
      addNotification('Could not build the PDF from that photo — ' + e.message, 'error');
    } finally {
      setBuilding(false);
    }
  };
  const handleScanPick = (e) => { addScanPage(e.target.files?.[0]); e.target.value = ''; };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  // Suggest downtime from the in/out dates unless the user already typed one.
  const suggestedDowntime = useMemo(() => {
    if (!form.dateIn || !form.dateOut) return null;
    const diff = Math.floor((new Date(form.dateOut) - new Date(form.dateIn)) / 86400000);
    return diff < 0 ? null : diff + 1;
  }, [form.dateIn, form.dateOut]);

  const costExGst = useMemo(() => {
    const n = parseFloat(form.cost);
    if (isNaN(n)) return 0;
    return incGst ? n / 1.1 : n;
  }, [form.cost, incGst]);

  const handleSave = async () => {
    if (!form.rego)   { addNotification('Select a vehicle', 'error'); return; }
    if (!form.type)   { addNotification('Select a job type', 'error'); return; }
    if (form.status === 'Completed' && !form.dateOut) { addNotification('A completed job needs a Date Out', 'error'); return; }
    setSaving(true);
    try {
      setSaveStep('Saving record…');
      const payload = {
        action: 'saveVehicleService',
        record: {
          ...form,
          rego: form.rego.toUpperCase(),
          cost: Math.round(costExGst * 100) / 100,
          odometer: parseFloat(form.odometer) || 0,
          downtimeDays: parseFloat(form.downtimeDays) || (form.status === 'Completed' ? (suggestedDowntime || 0) : 0),
          nextServiceKm: parseFloat(form.nextServiceKm) || 0,
        },
      };
      const res  = await fetch(VEHICLE_SERVICE_URL, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data.success) {
        addNotification(`Save failed: ${data.error || 'unknown error'}`, 'error');
        return;
      }
      const savedId = data.id || form.id;
      let saved = { ...payload.record, id: savedId };

      // Attach the invoice PDF after the row exists (the upload links to it).
      if (pdfFile) {
        setSaveStep(`Uploading invoice (${mb(pdfFile.size)} MB)…`);
        const failed = (why) => {
          // Keep the drawer open with the scan still in hand so Save retries it. The
          // row is already saved and `form.id` now holds its id, so the retry updates
          // that record instead of logging a second one — losing an hour-old paper
          // invoice to a closed modal is not a recoverable mistake.
          setAttachFailed(true);
          setForm(prev => ({ ...prev, id: savedId }));
          onSaved(saved);
          addNotification(`Record saved, but the invoice didn't attach: ${why}. The scan is still here — press Save to retry.`, 'error');
        };
        if (pdfFile.size > MAX_UPLOAD_BYTES) {
          failed(`${mb(pdfFile.size)} MB is over the ${mb(MAX_UPLOAD_BYTES)} MB limit`);
          return;
        }
        let upData;
        try {
          const pdfBase64 = await fileToBase64(pdfFile);
          // A phone on a weak connection can leave this fetch hanging with no error and
          // no spinner end-state; time it out so it fails visibly and can be retried.
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), UPLOAD_TIMEOUT_MS);
          try {
            const upRes = await fetch(VEHICLE_SERVICE_URL, {
              method: 'POST',
              signal: ctrl.signal,
              body: JSON.stringify({
                action: 'saveVehicleServiceFile',
                recordId: savedId,
                rego: payload.record.rego,
                filename: pdfFile.name,
                contentType: pdfFile.type || 'application/pdf',
                pdfBase64,
              }),
            });
            // An over-size body is rejected by the platform before the function runs, so
            // the reply is HTML and .json() throws — report the real cause, not a parse error.
            if (upRes.status === 413) { failed('the file was too big for the upload'); return; }
            upData = await upRes.json().catch(() => ({ success: false, error: `server returned ${upRes.status}` }));
          } finally {
            clearTimeout(timer);
          }
        } catch (upErr) {
          failed(upErr.name === 'AbortError' ? 'the upload timed out' : upErr.message);
          return;
        }
        if (!upData.success) { failed(upData.error || 'unknown error'); return; }
        saved = { ...saved, invoicePdfUrl: upData.viewUrl, invoiceFileId: upData.fileId };
        addNotification(`Invoice PDF attached — ${pdfFile.name}`, 'success');
      }

      setAttachFailed(false);
      onSaved(saved);
      addNotification(`${payload.record.rego} — ${form.type} ${isEdit ? 'updated' : 'logged'}`, 'success');
      onClose();
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
      setSaveStep('');
    }
  };

  const getDriver = login =>
    login ? personnel.find(p => p.loginName?.toUpperCase() === login?.toUpperCase()) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 560, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Service &amp; Repairs</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{isEdit ? `Edit — ${form.rego}` : 'Log Service / Repair'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <SectionTitle n={1} title="Tax Invoice — drop to auto-fill" />
        <div style={{ marginBottom: 20 }}>
          {form.invoicePdfUrl && !pdfFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
              <FileText size={14} color="var(--hp-gold)" />
              <a href={form.invoicePdfUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '0.8rem', color: 'var(--hp-text)', textDecoration: 'none', flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                View attached invoice <ExternalLink size={11} color="var(--hp-muted)" />
              </a>
              {pdfDownloadUrl(form) && (
                <a href={pdfDownloadUrl(form)} title="Download invoice PDF"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--hp-gold)', textDecoration: 'none', fontWeight: 600 }}>
                  <Download size={12} /> Download
                </a>
              )}
              <span style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>choose a file below to replace it</span>
            </div>
          )}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? 'var(--hp-gold)' : pdfFile ? 'var(--hp-green)' : 'var(--hp-gold)66'}`,
              borderRadius: 10, padding: '18px 14px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--hp-surface)' : 'transparent',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            {pdfFile ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-text)' }}>
                {extracting
                  ? <Loader size={14} color="var(--hp-gold)" style={{ animation: 'spin 1s linear infinite' }} />
                  : <Paperclip size={14} color="var(--hp-green)" />}
                <span style={{ fontWeight: 600 }}>{pdfFile.name}</span>
                <span style={{ color: 'var(--hp-muted)', fontSize: '0.72rem' }}>({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={e => { e.stopPropagation(); setPdfFile(null); setScanPages([]); setExtractedNote(''); setBuildNote(''); }}
                  title="Remove selected file"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
                  <FileText size={15} color="var(--hp-gold)" />
                  <span><strong style={{ color: 'var(--hp-text)' }}>Drop the invoice PDF here</strong> — or click to browse</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginTop: 5 }}>
                  PDF or photo, up to {MAX_PDF_MB} MB · the form below fills itself from the invoice · stored securely on save
                </div>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*"
            style={{ display: 'none' }} onChange={handleFilePick} />
          {/* Mobile scan — opens the device camera (side-button shutter), feeds the same
              auto-fill + store flow. Separate input from the drop zone so the camera fires
              directly on phones instead of the file picker. */}
          <label className="btn btn-secondary btn-sm"
            style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
            <Camera size={14} /> {scanPages.length ? `Add another page (page ${scanPages.length + 1})` : 'Scan invoice with camera'}
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleScanPick} />
          </label>
          {scanPages.length > 0 && (
            <>
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 6, textAlign: 'center' }}>
                {scanPages.length} page{scanPages.length > 1 ? 's' : ''} scanned — combined into one PDF on save. Tap “Add another page” for more, or the ✕ above to start over.
              </div>
              {(building || buildNote) && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4,
                  fontSize: '0.72rem',
                  color: building ? 'var(--hp-gold)'
                    : (pdfFile && pdfFile.size > MAX_UPLOAD_BYTES) ? 'var(--hp-red)' : 'var(--hp-green)',
                  textAlign: 'center',
                }}>
                  {building && <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} />}
                  {building ? (buildNote || 'Combining pages…') : buildNote}
                </div>
              )}
              <button type="button" className="btn btn-primary btn-sm" disabled={extracting || !pdfFile}
                onClick={() => pdfFile && autoFillFromInvoice(pdfFile)}
                style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <FileText size={14} /> Read all {scanPages.length} page{scanPages.length > 1 ? 's' : ''}
              </button>
            </>
          )}
          {extracting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.75rem', color: 'var(--hp-gold)' }}>
              <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
              Reading the invoice and filling the form…
            </div>
          )}
          {!extracting && extractedNote && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6, fontSize: '0.75rem', color: 'var(--hp-green)' }}>
              <CheckCircle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
              {extractedNote}
            </div>
          )}
          {!extracting && !extractedNote && pdfFile && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              Uploads when you hit Save
            </div>
          )}
        </div>

        <SectionTitle n={2} title="Vehicle & Job" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 6 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Vehicle *</label>
            <select className="input" style={inputStyle} value={form.rego} onChange={e => set('rego', e.target.value)} disabled={isEdit}>
              <option value="">— Select vehicle —</option>
              {vehicles.filter(v => !v.archived || v.rego === form.rego).map(v => {
                const driver = getDriver(v.login);
                const desc = [v.year, v.make, v.model].filter(Boolean).join(' ');
                return (
                  <option key={v.rego} value={v.rego}>
                    {v.rego}{desc ? ` — ${desc}` : ''}{driver ? ` (${driver.name})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Job Type *</label>
            <select className="input" style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
              {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Workshop / Provider</label>
            <input className="input" style={inputStyle} value={form.provider} list="hp-providers"
              onChange={e => set('provider', e.target.value)} placeholder={DEFAULT_PROVIDER} />
            <datalist id="hp-providers">
              {providers.map(p => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Work Description</label>
            <textarea className="input" rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="e.g. 60,000 km scheduled service — oil, filters, brake check" />
          </div>
        </div>

        <SectionTitle n={3} title="Dates & Downtime" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 6 }}>
          <div>
            <label style={labelStyle}>Booked For</label>
            <input type="date" className="input" style={inputStyle} value={form.bookedDate} onChange={e => set('bookedDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Date In</label>
            <input type="date" className="input" style={inputStyle} value={form.dateIn} onChange={e => set('dateIn', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Date Out</label>
            <input type="date" className="input" style={inputStyle} value={form.dateOut} onChange={e => set('dateOut', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Odometer (km)</label>
            <input type="number" className="input" style={inputStyle} value={form.odometer} onChange={e => set('odometer', e.target.value)} placeholder="e.g. 61250" />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Days Off Road</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" step="0.5" min="0" className="input" style={{ ...inputStyle, width: 110 }}
                value={form.downtimeDays} onChange={e => set('downtimeDays', e.target.value)}
                placeholder={suggestedDowntime !== null ? String(suggestedDowntime) : '0'} />
              {suggestedDowntime !== null && String(form.downtimeDays) !== String(suggestedDowntime) && (
                <button className="btn btn-ghost btn-sm" onClick={() => set('downtimeDays', suggestedDowntime)}>
                  Use {suggestedDowntime}d from dates
                </button>
              )}
            </div>
          </div>
        </div>

        <SectionTitle n={4} title="Cost" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 6 }}>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input type="number" step="0.01" className="input" style={inputStyle} value={form.cost} onChange={e => set('cost', e.target.value)} placeholder="0.00" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, fontSize: '0.75rem', color: 'var(--hp-dim)', cursor: 'pointer' }}>
              <input type="checkbox" checked={incGst} onChange={e => setIncGst(e.target.checked)} />
              Amount includes GST
            </label>
            {incGst && parseFloat(form.cost) > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 4 }}>
                Stored ex GST: {fmtMoney(costExGst)}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Invoice No</label>
            <input className="input" style={inputStyle} value={form.invoiceNo} onChange={e => set('invoiceNo', e.target.value)} placeholder="Workshop invoice ref" />
          </div>
        </div>

        <SectionTitle n={5} title="Next Service Due" />
        <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: 'var(--hp-muted)' }}>
          Saving a <strong style={{ color: 'var(--hp-text)' }}>Completed</strong> job with a next-due date also updates the Fleet page's service alerts.
        </p>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 6 }}>
          <div>
            <label style={labelStyle}>Next Service Date</label>
            <input type="date" className="input" style={inputStyle} value={form.nextServiceDate} onChange={e => set('nextServiceDate', e.target.value)} />
            <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => set('nextServiceDate', addMonthsISO(form.dateOut || form.dateIn || todayISO(), 6))}>+6 mo</button>
              <button className="btn btn-ghost btn-sm" onClick={() => set('nextServiceDate', addMonthsISO(form.dateOut || form.dateIn || todayISO(), 12))}>+12 mo</button>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Next Service KM</label>
            <input type="number" className="input" style={inputStyle} value={form.nextServiceKm} onChange={e => set('nextServiceKm', e.target.value)} placeholder="e.g. 75000" />
            {parseFloat(form.odometer) > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => set('nextServiceKm', (parseFloat(form.odometer) || 0) + 15000)}>+15,000</button>
                <button className="btn btn-ghost btn-sm" onClick={() => set('nextServiceKm', (parseFloat(form.odometer) || 0) + 30000)}>+30,000</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={labelStyle}>Notes</label>
          <textarea className="input" rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Warranty claims, parts on order, loan vehicle, etc." />
        </div>

        <SectionTitle n={6} title="Visibility" />
        <div style={{ display: 'flex', gap: 8, marginBottom: 26 }}>
          {[
            { v: 'admin', title: 'Admin only', desc: 'Office admins only.' },
            { v: 'operator', title: 'Exposed for viewing', desc: 'Admin + the van’s current driver.' },
          ].map(o => {
            const on = (form.visibility || 'admin') === o.v;
            return (
              <button key={o.v} type="button" onClick={() => set('visibility', o.v)}
                style={{ flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                  background: on ? 'rgba(201,168,76,0.15)' : 'var(--hp-surface)' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)' }}>{o.title}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 2 }}>{o.desc}</div>
              </button>
            );
          })}
        </div>

        {attachFailed && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, padding: '10px 12px',
            borderRadius: 8, border: '1.5px solid var(--hp-red)', background: 'rgba(220,80,80,0.12)',
            fontSize: '0.76rem', color: 'var(--hp-text)',
          }}>
            <AlertTriangle size={14} color="var(--hp-red)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong>The record is saved — the invoice isn’t attached yet.</strong> Your scan is still
              loaded above; press <strong>Save record</strong> to retry the upload. Closing this drawer
              loses the scan and you’d have to photograph the invoice again.
            </span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving || building}>
            <Save size={14} /> {saving ? (saveStep || 'Saving…') : building ? 'Preparing scan…' : attachFailed ? 'Save record — retry upload' : 'Save record'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Back-on-road modal ────────────────────────────────────────────────────────
// One-click close of an open workshop record: pick the day it went back on the
// road, and the record flips to Completed so the van drops off the alerts.
function BackOnRoadModal({ record, driverName, onClose, onDone, addNotification }) {
  const [dateOut, setDateOut] = useState(todayISO());
  const [saving, setSaving]   = useState(false);

  const days = useMemo(() => {
    if (!record.dateIn || !dateOut) return null;
    return Math.max(0, Math.floor((new Date(dateOut) - new Date(record.dateIn)) / 86400000));
  }, [record.dateIn, dateOut]);

  const handleConfirm = async () => {
    if (!dateOut) { addNotification('Pick the date it went back on the road', 'error'); return; }
    if (record.dateIn && dateOut < record.dateIn) { addNotification('Back-on-road date is before the date it went in', 'error'); return; }
    setSaving(true);
    try {
      const saved = {
        ...record,
        status: 'Completed',
        dateOut,
        downtimeDays: days != null ? days : (record.downtimeDays || 0),
      };
      const res  = await fetch(VEHICLE_SERVICE_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'saveVehicleService', record: saved }),
      });
      const data = await res.json();
      if (!data.success) { addNotification(`Failed: ${data.error || 'unknown error'}`, 'error'); return; }
      onDone({ ...saved, id: data.id || record.id });
      addNotification(`${record.rego} back on the road — ${record.type || 'job'} closed${days != null ? ` (${days}d off road)` : ''}`, 'success');
      onClose();
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 440, padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.63rem', textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--hp-green)', marginBottom: 5 }}>Service · Back on road</div>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{record.rego} back on the road</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--hp-muted)', margin: '8px 0 18px' }}>
          Closes the open <strong style={{ color: 'var(--hp-text)' }}>{record.type || 'workshop'}</strong> record
          {record.provider ? ` at ${record.provider}` : ''}{driverName ? ` — now with ${driverName}` : ''}. Costs or an invoice can be added later.
        </p>

        <label style={labelStyle}>Date back on road</label>
        <input type="date" className="input" style={inputStyle} value={dateOut} max={todayISO()} onChange={e => setDateOut(e.target.value)} />
        <div style={{ fontSize: '0.75rem', color: 'var(--hp-muted)', margin: '7px 0 22px' }}>
          {record.dateIn ? <>Went in {fmtDate(record.dateIn)}{days != null && <> · <strong style={{ color: 'var(--hp-text)' }}>{days} day{days !== 1 ? 's' : ''}</strong> off road</>}</> : 'No date-in recorded on this job.'}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirm} disabled={saving}>
            <CheckCircle size={14} /> {saving ? 'Saving…' : 'Mark back on road'}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Maintenance reminders ─────────────────────────────────────────────────────
// "Brakes in about 5,000–10,000 km" held against the odometer until it's done. Set here;
// the van's driver sees it on their Van Check + My Van every day, and the morning job
// (vehicle-watch-alerts) texts + pushes the driver and the office as it closes in.

const WATCH_ITEMS = ['Brakes', 'Tyres', 'Battery', 'Clutch', 'Wiper blades', 'Suspension'];
const WATCH_TONE = {
  watch: { bg: 'var(--hp-surface)', fg: 'var(--hp-dim)', border: 'var(--hp-border)', word: 'Watching' },
  soon:  { bg: 'rgba(245,158,11,0.14)', fg: 'var(--hp-amber)', border: 'var(--hp-amber)', word: 'Due soon' },
  due:   { bg: 'rgba(245,158,11,0.22)', fg: 'var(--hp-amber)', border: 'var(--hp-amber)', word: 'Due now' },
  over:  { bg: 'rgba(239,68,68,0.16)', fg: 'var(--hp-red)', border: 'var(--hp-red)', word: 'Overdue' },
};
const kmIn = (v) => { const n = parseInt(String(v || '').replace(/[^0-9]/g, ''), 10); return Number.isFinite(n) ? n : 0; };
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return ''; } };

function WatchChip({ w, st, onClick }) {
  const t = WATCH_TONE[st.stage] || WATCH_TONE.watch;
  return (
    <button onClick={onClick} title={`${w.item} — ${watchTarget(w)}${st.label ? ` · ${st.label}` : ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, marginRight: 4, padding: '2px 8px', borderRadius: 20, cursor: 'pointer',
        fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap', background: t.bg, color: t.fg, border: `1px solid ${t.border}` }}>
      <Bell size={10} /> {w.item}{st.label ? ` · ${st.label}` : ''}
    </button>
  );
}

function WatchModal({ rego, vehicleText, driver, odo, lastService, watches, onClose, onChanged, addNotification }) {
  const open = watches.filter(w => w.status === 'open');
  const closed = watches.filter(w => w.status !== 'open');
  const curKm = odo?.odometer || 0;
  const svcKm = lastService?.odometer || 0;
  const blank = () => ({ id: '', item: '', inFrom: '', inTo: '', basis: curKm ? 'odo' : (svcKm ? 'service' : 'custom'), customBase: '', leadKm: String(DEFAULT_LEAD_KM), dueDate: '', note: '', tellDriver: !!driver });
  const [form, setForm] = useState(open.length ? null : blank());
  const [busy, setBusy] = useState('');
  const [closing, setClosing] = useState(null);   // { id, status }
  const [closeNote, setCloseNote] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const baseKm = form ? (form.basis === 'odo' ? curKm : form.basis === 'service' ? svcKm : kmIn(form.customBase)) : 0;
  const fromKm = form && kmIn(form.inFrom) ? baseKm + kmIn(form.inFrom) : 0;
  const toKm = form && kmIn(form.inTo) ? baseKm + kmIn(form.inTo) : fromKm;
  const preview = form && (fromKm || form.dueDate)
    ? watchStanding({ fromKm, toKm, leadKm: kmIn(form.leadKm), dueDate: form.dueDate }, curKm, todayISO()) : null;

  const call = async (body) => {
    const res = await fetch(WATCH_URL, { method: 'POST', body: JSON.stringify(body) });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'unknown error');
    return data;
  };
  const toldText = (t) => !t ? '' : !t.driver ? ' — no driver on this van' : (t.sms || t.push) ? ` — ${t.driver} ${[t.sms && 'texted', t.push && 'pushed'].filter(Boolean).join(' + ')}` : ` — couldn't reach ${t.driver} (no mobile / alerts off)`;

  const save = async () => {
    if (!form.item.trim()) { addNotification('Say what needs doing', 'error'); return; }
    if (form.basis === 'custom' && !kmIn(form.customBase) && kmIn(form.inFrom)) { addNotification('Enter the odometer to count from', 'error'); return; }
    if (!fromKm && !form.dueDate) { addNotification('Give a distance or a date', 'error'); return; }
    if (toKm - baseKm > 100000) { addNotification(`That's ${fmtKm(toKm - baseKm)} away — check the numbers`, 'error'); return; }
    if (toKm && toKm < fromKm) { addNotification('The second distance should be the bigger one', 'error'); return; }
    setBusy('save');
    try {
      const baseNote = form.basis === 'odo' ? `odometer ${fmtKm(curKm)}${odo?.date ? ` on ${fmtDate(odo.date)}` : ''}`
        : form.basis === 'service' ? `last service ${fmtKm(svcKm)}${lastService ? ` on ${fmtDate(recordDate(lastService))}` : ''}` : `${fmtKm(baseKm)} (entered)`;
      const d = await call({ action: 'save', tellDriver: form.tellDriver && !!driver, watch: {
        id: form.id || undefined, rego, item: form.item.trim(), note: form.note, fromKm, toKm,
        leadKm: kmIn(form.leadKm) || DEFAULT_LEAD_KM, dueDate: form.dueDate, baseKm: fromKm ? baseKm : 0, baseNote: fromKm ? baseNote : '',
      } });
      onChanged(d.watch);
      addNotification(`${rego} · ${d.watch.item} reminder ${form.id ? 'updated' : 'set'}${toldText(d.told)}`, 'success');
      setForm(null);
    } catch (e) { addNotification(`Could not save: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };
  const tell = async (w) => {
    setBusy('tell-' + w.id);
    try { const d = await call({ action: 'notify', id: w.id }); onChanged(d.watch); addNotification(`${rego} · ${w.item}${toldText(d.told)}`, d.told && (d.told.sms || d.told.push) ? 'success' : 'error'); }
    catch (e) { addNotification(`Could not send: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };
  const setStatus = async (w, status, note = '') => {
    setBusy('close-' + w.id);
    try {
      const d = await call({ action: 'close', id: w.id, status, note });
      onChanged(d.watch); setClosing(null); setCloseNote('');
      addNotification(`${rego} · ${w.item} ${status === 'done' ? 'marked done' : status === 'cancelled' ? 'cancelled' : 'reopened'}`, 'success');
    } catch (e) { addNotification(`Could not update: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };
  const edit = (w) => {
    const b = w.baseKm || 0;
    setForm({ id: w.id, item: w.item, basis: 'custom', customBase: b ? String(b) : '', inFrom: w.fromKm ? String(w.fromKm - b) : '', inTo: w.toKm && w.toKm !== w.fromKm ? String(w.toKm - b) : '',
      leadKm: String(w.leadKm || DEFAULT_LEAD_KM), dueDate: w.dueDate || '', note: w.note || '', tellDriver: false });
  };

  const small = { fontSize: '0.74rem', color: 'var(--hp-muted)' };
  const numInput = { ...inputStyle, width: 110, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const basisBtn = (key, text, disabled) => (
    <button key={key} disabled={disabled} onClick={() => setForm({ ...form, basis: key })}
      className={`btn btn-sm ${form.basis === key ? 'btn-primary' : 'btn-secondary'}`} style={{ opacity: disabled ? 0.4 : 1 }}>{text}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div className="card" style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.63rem', textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--hp-gold)', marginBottom: 5 }}>Service · Reminders</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>{rego}</span> {vehicleText && <span style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--hp-muted)' }}>{vehicleText}</span>}</h3>
            <div style={{ ...small, marginTop: 4 }}>
              {curKm ? <>Odometer <strong style={{ color: 'var(--hp-text)' }}>{fmtKm(curKm)}</strong>{odo?.date && ` as at ${fmtDate(odo.date)}`}</> : 'No odometer reading yet'}
              {' · '}{driver ? <>Driver <strong style={{ color: 'var(--hp-text)' }}>{driver}</strong></> : 'No driver on this van'}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>

        {/* Open reminders */}
        {open.length > 0 && (
          <div style={{ marginTop: 18 }}>
            {open.map(w => {
              const st = watchStanding(w, curKm, todayISO());
              const t = WATCH_TONE[st.stage];
              const told = Object.entries(w.alerted || {}).filter(([k]) => k !== 'odoCheck').map(([, v]) => v).sort().pop();
              return (
                <div key={w.id} style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Bell size={14} color={t.fg} />
                    <strong style={{ fontSize: '0.92rem' }}>{w.item}</strong>
                    <Pill bg={st.stage === 'watch' ? 'rgba(0,0,0,0)' : t.fg} color={st.stage === 'watch' ? 'var(--hp-dim)' : st.stage === 'over' ? '#fff' : '#000'}>{t.word}</Pill>
                  </div>
                  <div style={{ fontSize: '0.82rem', marginTop: 5 }}>
                    {watchTarget(w)}{st.label && <span style={{ color: t.fg, fontWeight: 600 }}> · {st.label}</span>}
                  </div>
                  <div style={{ ...small, marginTop: 3 }}>
                    {w.baseNote && <>Counted from {w.baseNote}. </>}Warns {fmtKm(w.leadKm || DEFAULT_LEAD_KM)} before.
                    {w.note && <div style={{ color: 'var(--hp-text)', marginTop: 3 }}>{w.note}</div>}
                    <div style={{ marginTop: 3 }}>Set {fmtWhen(w.createdAt)} by {w.createdBy}{told ? ` · driver last told ${fmtWhen(told)}` : driver ? ' · driver not texted yet (sees it on Van Check)' : ''}</div>
                  </div>
                  {closing?.id === w.id ? (
                    <div style={{ marginTop: 10 }}>
                      <input className="input" style={inputStyle} autoFocus value={closeNote} onChange={e => setCloseNote(e.target.value)}
                        placeholder={closing.status === 'done' ? 'What was done? e.g. front pads + rotors, Col Crawford' : 'Why cancel? e.g. workshop re-checked, fine'} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button className="btn btn-primary btn-sm" disabled={!closeNote.trim() || !!busy} onClick={() => setStatus(w, closing.status, closeNote)}>
                          <CheckCircle size={12} /> {closing.status === 'done' ? 'Mark done' : 'Cancel reminder'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setClosing(null); setCloseNote(''); }}>Back</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => setClosing({ id: w.id, status: 'done' })}><CheckCircle size={12} /> Done</button>
                      {driver && (
                        <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => tell(w)} title={`Text + push ${driver} about this now`}>
                          {busy === 'tell-' + w.id ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <BellRing size={12} />} Tell {driver}
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => edit(w)}><Pencil size={12} /> Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setClosing({ id: w.id, status: 'cancelled' })}><X size={12} /> Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* New / edit form */}
        {form ? (
          <div style={{ marginTop: 18, borderTop: open.length ? '1px solid var(--hp-border)' : 'none', paddingTop: open.length ? 16 : 0 }}>
            <label style={labelStyle}>{form.id ? 'Edit reminder' : 'What needs doing?'}</label>
            <input className="input" style={inputStyle} value={form.item} placeholder="e.g. Brakes" onChange={e => setForm({ ...form, item: e.target.value })} />
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
              {WATCH_ITEMS.map(x => <button key={x} className={`btn btn-sm ${form.item === x ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: '0.72rem', padding: '3px 9px' }} onClick={() => setForm({ ...form, item: x })}>{x}</button>)}
            </div>

            <label style={{ ...labelStyle, marginTop: 16 }}>When — in about</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input className="input" style={numInput} inputMode="numeric" placeholder="5,000" value={form.inFrom} onChange={e => setForm({ ...form, inFrom: e.target.value })} />
              <span style={small}>to</span>
              <input className="input" style={numInput} inputMode="numeric" placeholder="10,000" value={form.inTo} onChange={e => setForm({ ...form, inTo: e.target.value })} />
              <span style={small}>km, counted from</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
              {basisBtn('odo', curKm ? `Odometer now · ${fmtKm(curKm)}` : 'Odometer now', !curKm)}
              {basisBtn('service', svcKm ? `Last service · ${fmtKm(svcKm)}` : 'Last service', !svcKm)}
              {basisBtn('custom', 'A reading')}
              {form.basis === 'custom' && <input className="input" style={numInput} inputMode="numeric" placeholder="odometer" value={form.customBase} onChange={e => setForm({ ...form, customBase: e.target.value })} />}
            </div>
            {fromKm > 0 && (
              <div style={{ marginTop: 10, padding: '9px 12px', borderRadius: 8, background: 'var(--hp-surface)', fontSize: '0.82rem' }}>
                Due at <strong>{watchTarget({ fromKm, toKm })}</strong> on the odometer
                {preview?.label && <span style={{ color: WATCH_TONE[preview.stage].fg }}> · {preview.label}</span>}
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14 }}>
              <div>
                <label style={labelStyle}>Start warning</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input className="input" style={numInput} inputMode="numeric" value={form.leadKm} onChange={e => setForm({ ...form, leadKm: e.target.value })} />
                  <span style={small}>km before</span>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Or by date <span style={{ ...small, fontWeight: 400 }}>(optional)</span></label>
                <input type="date" className="input" style={{ ...inputStyle, width: 170 }} value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
              </div>
            </div>

            <label style={{ ...labelStyle, marginTop: 14 }}>Note <span style={{ ...small, fontWeight: 400 }}>(the driver sees this)</span></label>
            <input className="input" style={inputStyle} value={form.note} placeholder="e.g. Flagged by Col Crawford at the 15 Sep service — front pads" onChange={e => setForm({ ...form, note: e.target.value })} />

            <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', fontSize: '0.8rem', lineHeight: 1.5 }}>
              {driver ? (
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.tellDriver} onChange={e => setForm({ ...form, tellDriver: e.target.checked })} style={{ marginTop: 3 }} />
                  <span><strong>Text + push {driver} now</strong><br /><span style={small}>
                    Either way it's on their Van Check and My Van from now on, and they're texted again when it's due soon, due, and overdue.
                  </span></span>
                </label>
              ) : <span style={small}>No driver on this van — the office gets the alerts. Whoever takes the van sees it on their Van Check.</span>}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={!!busy}>
                {busy === 'save' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Bell size={14} />} {form.id ? 'Save changes' : 'Set reminder'}
              </button>
              {(open.length > 0 || form.id) && <button className="btn btn-secondary" onClick={() => setForm(null)}>Back</button>}
            </div>
          </div>
        ) : (
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setForm(blank())}><Plus size={12} /> Add another reminder</button>
        )}

        {/* Closed — kept, never deleted */}
        {closed.length > 0 && (
          <div style={{ marginTop: 18, borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowClosed(s => !s)}>
              {showClosed ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {closed.length} closed reminder{closed.length === 1 ? '' : 's'}
            </button>
            {showClosed && closed.map(w => (
              <div key={w.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid var(--hp-border)22', fontSize: '0.8rem' }}>
                <div style={{ flex: 1 }}>
                  <strong>{w.item}</strong> <span style={small}>· {watchTarget(w)}</span>
                  <div style={small}>{w.status === 'done' ? 'Done' : 'Cancelled'} {fmtWhen(w.closedAt)} by {w.closedBy}{w.closeNote ? ` — ${w.closeNote}` : ''}</div>
                </div>
                <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => setStatus(w, 'open')}>Reopen</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function VehicleServicePage() {
  const { vehicles, setVehicles, personnel, addNotification, inspectionAlertCount, refreshInspectionAlerts } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [records, setRecords]   = useState([]);
  const [latestOdos, setLatestOdos] = useState({}); // rego → { odometer, date } from odometer_log
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab]           = useState('schedule'); // schedule | history | costs
  const [drawer, setDrawer]     = useState(null);
  const [backOnRoad, setBackOnRoad] = useState(null); // open record being closed
  const [deleting, setDeleting] = useState(new Set());
  const [watches, setWatches]   = useState([]);     // maintenance reminders, every status
  const [watchRego, setWatchRego] = useState(null); // rego whose reminders modal is open

  // History filters
  const [search, setSearch]           = useState(searchParams.get('rego') || '');
  // Schedule/Costs scope filters. Owner sticks across visits — Paul services the
  // PHB / HPL / SG Fleet vans himself, Owner Driver vans are the drivers' problem.
  const [ownerF, setOwnerF] = useState(() => { try { return localStorage.getItem('vsvcOwnerFilter') || ''; } catch { return ''; } });
  const [dueF, setDueF]     = useState(''); // '' | overdue | soon | ok | open | none
  useEffect(() => { try { localStorage.setItem('vsvcOwnerFilter', ownerF); } catch { /* private mode */ } }, [ownerF]);
  const [typeFilter, setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showRetired, setShowRetired] = useState(false);

  // Inspection register (Inspections tab)
  const [inspections, setInspections] = useState([]);
  const [inspLoaded, setInspLoaded]   = useState(false);
  const [inspLoading, setInspLoading] = useState(false);
  const [inspErr, setInspErr]         = useState(null);
  const [inspDriver, setInspDriver]   = useState('');
  const [inspRego, setInspRego]       = useState('');
  const [inspFrom, setInspFrom]       = useState('');
  const [inspTo, setInspTo]           = useState('');
  const [inspStatusF, setInspStatusF] = useState('');   // '' | overdue | soon | ok
  const [inspFaults, setInspFaults]   = useState(false);
  // Persistent open issues (carried across days until resolved with a remedy)
  const [issues, setIssues]           = useState(null);
  const [issuesLoaded, setIssuesLoaded] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [resolveId, setResolveId]     = useState(null);
  const [remedyText, setRemedyText]   = useState('');
  const [inspSort, setInspSort]       = useState({ field: 'inspectionDate', dir: 'desc' });
  const [inspDetail, setInspDetail]   = useState(null);  // { row, full, loading }

  // Deep link: /vehicle-service?rego=XX lands on the history tab pre-filtered.
  useEffect(() => {
    if (searchParams.get('rego')) setTab('history');
  }, []);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res  = await fetch(VEHICLE_SERVICE_URL, { method: 'POST', body: JSON.stringify({ action: 'getVehicleService' }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'unknown error');
      setRecords(data.records || []);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // Current odometer per rego — the highest reading in odometer_log (drivers'
  // shift-start/end kms + van checks all land there). Non-blocking: the page
  // renders without it and the Odometer column fills in when it arrives.
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res  = await fetch(SHIFT_ODO_URL, { method: 'POST', body: JSON.stringify({ action: 'getLatestOdos' }) });
        const data = await res.json();
        if (!gone && data.success) setLatestOdos(data.odos || {});
      } catch (_) { /* column just stays blank */ }
    })();
    return () => { gone = true; };
  }, []);

  // Maintenance reminders — non-blocking like the odometers; the chips fill in when it lands.
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res  = await fetch(WATCH_URL, { method: 'POST', body: JSON.stringify({ action: 'list' }) });
        const data = await res.json();
        if (!gone && data.success) setWatches(data.watches || []);
      } catch (_) { /* no chips, page still works */ }
    })();
    return () => { gone = true; };
  }, []);
  const upsertWatch = (w) => setWatches(prev => prev.some(x => x.id === w.id) ? prev.map(x => x.id === w.id ? w : x) : [...prev, w]);
  // Open reminders per rego with their standing against the same current odometer the
  // Odometer column shows — so a chip and the column can never disagree.
  const openWatchesByRego = useMemo(() => {
    const m = {};
    const today = todayISO();
    watches.filter(w => w.status === 'open').forEach(w => {
      (m[w.rego] = m[w.rego] || []).push({ w, st: watchStanding(w, latestOdos[w.rego]?.odometer, today) });
    });
    return m;
  }, [watches, latestOdos]);

  const getDriver = login =>
    login ? personnel.find(p => p.loginName?.toUpperCase() === login?.toUpperCase()) : null;

  const vehicleLabel = (rego) => {
    const v = vehicles.find(x => x.rego === rego);
    if (!v) return '';
    return [v.year, v.make, v.model].filter(Boolean).join(' ');
  };

  // A retired van's jobs stay in the history (that's the point of keeping the
  // records), but the row has to SAY the van is gone — otherwise an old service
  // on a traded-in van reads as a live one still owed a follow-up.
  const retiredByRego = useMemo(() => {
    const m = {};
    vehicles.filter(v => v.archived).forEach(v => { m[v.rego] = v; });
    return m;
  }, [vehicles]);

  const providers = useMemo(() => {
    const set = new Set([DEFAULT_PROVIDER]);
    records.forEach(r => { if (r.provider) set.add(r.provider); });
    return [...set].sort();
  }, [records]);

  const fyStart = fyStartISO();

  // Canonical next-service odometer per rego, derived from the latest scheduled
  // service + the vehicle's service interval (see deriveNextServiceKm). This is the
  // single source of truth for "km to go / km over" — the raw next_service_km stored
  // on a service or snapshotted on a van check is never trusted directly.
  const nextKmByRego = useMemo(() => {
    const m = {};
    vehicles.forEach(v => { m[v.rego] = deriveNextServiceKm(records, v.rego, v.serviceIntervalKm); });
    return m;
  }, [vehicles, records]);

  // ── Per-vehicle summary (drives the schedule tab + alerts) ──
  // Retired (archived) vehicles keep their history but must not generate
  // service alerts, schedule rows or due/overdue counts.
  const summaries = useMemo(() => {
    return vehicles.filter(v => !v.archived).map(v => {
      const recs = records.filter(r => r.rego === v.rego);
      const completed = recs.filter(r => r.status === 'Completed')
        .sort((a, b) => (recordDate(b) || '').localeCompare(recordDate(a) || ''));
      const services = completed.filter(r => r.type === 'Scheduled Service');
      const lastService = services[0] || null;
      const openRecord = recs.find(isOpen) || null;

      // Next due: most recent completed service's next-due, else Fleet sheet date.
      const nextDue   = lastService?.nextServiceDate || v.service_due || '';
      const nextDueKm = nextKmByRego[v.rego] || 0;
      const currentOdo = latestOdos[v.rego] || null;           // { odometer, date }
      const kmLeft = (nextDueKm > 0 && currentOdo?.odometer > 0) ? nextDueKm - currentOdo.odometer : null;
      const fyRecs    = recs.filter(r => (recordDate(r) || '') >= fyStart);
      const fyCost    = fyRecs.reduce((s, r) => s + (r.cost || 0), 0);
      const fyDowntime = fyRecs.reduce((s, r) => s + (r.downtimeDays || 0), 0)
        + recs.filter(r => r.status === 'In Workshop').reduce((s, r) => s + liveDowntime(r), 0);

      return {
        rego: v.rego, vehicle: v, lastService, openRecord,
        nextDue, nextDueKm, currentOdo, kmLeft, dueDays: daysUntil(nextDue),
        jobCount: recs.length, fyCost, fyDowntime,
        allCost: recs.reduce((s, r) => s + (r.cost || 0), 0),
        allDowntime: recs.reduce((s, r) => s + (r.downtimeDays || 0), 0),
      };
    }).sort((a, b) => {
      // In-workshop first, then by due date urgency
      const aw = a.openRecord?.status === 'In Workshop' ? 0 : 1;
      const bw = b.openRecord?.status === 'In Workshop' ? 0 : 1;
      if (aw !== bw) return aw - bw;
      // Overdue on kms counts as overdue even when the date still looks fine.
      const eff = (s) => {
        let d = s.dueDays === null ? 99999 : s.dueDays;
        if (s.kmLeft != null && s.kmLeft < 0) d = Math.min(d, -1);
        return d;
      };
      return eff(a) - eff(b);
    });
  }, [vehicles, records, fyStart, nextKmByRego, latestOdos]);

  // Owner scope — drives the stats tiles, the alert panel and both tables, so
  // "Ours to service" turns the whole page into Paul's servicing worklist.
  const owners = useMemo(
    () => [...new Set(vehicles.filter(v => !v.archived).map(v => (v.owner || '').trim()).filter(Boolean))].sort(),
    [vehicles]);
  const scopedSummaries = useMemo(() => {
    if (!ownerF) return summaries;
    const ours = ['PHB', 'HPL', 'SG FLEET'];
    return summaries.filter(s => {
      const o = (s.vehicle.owner || '').trim().toUpperCase();
      return ownerF === 'OURS' ? ours.includes(o) : o === ownerF.toUpperCase();
    });
  }, [summaries, ownerF]);

  // Due standing, mirroring the Status pill: an open job trumps the countdown.
  const dueClass = (s) => {
    if (s.openRecord) return 'open';
    if ((s.dueDays !== null && s.dueDays < 0) || (s.kmLeft != null && s.kmLeft < 0)) return 'overdue';
    if ((s.dueDays !== null && s.dueDays <= 30) || (s.kmLeft != null && s.kmLeft <= 500)) return 'soon';
    if (!s.nextDue && s.kmLeft == null) return 'none';
    return 'ok';
  };
  const watchAlerts = useMemo(() => {
    const scoped = new Set(scopedSummaries.map(s => s.rego));
    return Object.entries(openWatchesByRego).filter(([r]) => scoped.has(r))
      .flatMap(([, list]) => list).filter(x => x.st.stage !== 'watch');
  }, [openWatchesByRego, scopedSummaries]);

  // Schedule + Costs tabs: due-standing filter + free-text search over rego,
  // driver name, AP login or any invoice number on the vehicle's service records.
  const filteredSummaries = useMemo(() => {
    const q = search.trim().toUpperCase();
    return scopedSummaries.filter(s => {
      if (dueF === 'reminder') { if (!openWatchesByRego[s.rego]) return false; }
      else if (dueF && dueClass(s) !== dueF) return false;
      if (!q) return true;
      const driver = getDriver(s.vehicle.login);
      return s.rego.toUpperCase().includes(q)
        || (s.vehicle.login || '').toUpperCase().includes(q)
        || (driver?.name || '').toUpperCase().includes(q)
        || records.some(r => r.rego === s.rego && (r.invoiceNo || '').toUpperCase().includes(q));
    });
  }, [scopedSummaries, dueF, search, personnel, records, openWatchesByRego]);

  // ── Stats ── (scoped to the owner filter, so the tiles read as "my fleet's health")
  const stats = useMemo(() => {
    const scopedRegos = new Set(scopedSummaries.map(s => s.rego));
    const scopedRecs  = records.filter(r => scopedRegos.has(r.rego));
    const offRoad  = scopedRecs.filter(r => r.status === 'In Workshop').length;
    const overdue  = scopedSummaries.filter(s => ((s.dueDays !== null && s.dueDays < 0) || (s.kmLeft != null && s.kmLeft < 0)) && !s.openRecord).length;
    const dueSoon  = scopedSummaries.filter(s => s.dueDays !== null && s.dueDays >= 0 && s.dueDays <= 30 && !s.openRecord).length;
    const fyRecs   = scopedRecs.filter(r => (recordDate(r) || '') >= fyStart);
    const fyCost   = fyRecs.reduce((s, r) => s + (r.cost || 0), 0);
    const fyDowntime = fyRecs.reduce((s, r) => s + (r.downtimeDays || 0), 0)
      + scopedRecs.filter(r => r.status === 'In Workshop').reduce((s, r) => s + liveDowntime(r), 0);
    return { offRoad, overdue, dueSoon, fyCost, fyDowntime };
  }, [records, scopedSummaries, fyStart]);

  // ── History rows ──
  const historyRows = useMemo(() => {
    const q = search.trim().toUpperCase();
    return records
      .filter(r => {
        // A retired van is off the road for good — its jobs (even one left
        // "In Workshop") don't belong in the working list. They stay reachable:
        // tick "Show retired", or search the rego (the Fleet page deep link).
        if (retiredByRego[r.rego] && !showRetired && r.rego !== q) return false;
        if (typeFilter && r.type !== typeFilter) return false;
        if (statusFilter && r.status !== statusFilter) return false;
        if (!q) return true;
        const driver = getDriver(vehicles.find(v => v.rego === r.rego)?.login);
        return r.rego.includes(q)
          || (r.provider || '').toUpperCase().includes(q)
          || (r.description || '').toUpperCase().includes(q)
          || (r.invoiceNo || '').toUpperCase().includes(q)
          || (driver?.name || '').toUpperCase().includes(q);
      })
      .sort((a, b) => {
        const ao = isOpen(a) && !retiredByRego[a.rego] ? 0 : 1, bo = isOpen(b) && !retiredByRego[b.rego] ? 0 : 1;
        if (ao !== bo) return ao - bo;
        return (recordDate(b) || '').localeCompare(recordDate(a) || '');
      });
  }, [records, search, typeFilter, statusFilter, vehicles, personnel, retiredByRego, showRetired]);

  const retiredRecordCount = useMemo(
    () => records.filter(r => retiredByRego[r.rego]).length,
    [records, retiredByRego]);

  // ── Cost analytics ──
  const costByMonth = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: toLocalISO(d).slice(0, 7), label: d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }), cost: 0, jobs: 0 });
    }
    records.forEach(r => {
      const m = months.find(x => (recordDate(r) || '').startsWith(x.key));
      if (m) { m.cost += r.cost || 0; m.jobs += 1; }
    });
    return months;
  }, [records]);

  const costByType = useMemo(() => {
    const map = {};
    records.filter(r => (recordDate(r) || '') >= fyStart).forEach(r => {
      const t = r.type || 'Other';
      map[t] = map[t] || { type: t, cost: 0, jobs: 0 };
      map[t].cost += r.cost || 0;
      map[t].jobs += 1;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [records, fyStart]);

  const costByProvider = useMemo(() => {
    const map = {};
    records.forEach(r => {
      const name = (r.provider || '').trim() || 'No workshop recorded';
      const key = name.toUpperCase();
      if (!map[key]) map[key] = { provider: name, jobs: 0, fyJobs: 0, cost: 0, fyCost: 0 };
      map[key].jobs += 1;
      map[key].cost += r.cost || 0;
      if ((recordDate(r) || '') >= fyStart) {
        map[key].fyJobs += 1;
        map[key].fyCost += r.cost || 0;
      }
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [records, fyStart]);

  const providerTotal = Math.max(1, costByProvider.reduce((s, p) => s + p.cost, 0));

  const maxMonthCost = Math.max(1, ...costByMonth.map(m => m.cost));

  // ── Inspection register ──
  const loadInspections = useCallback(async () => {
    setInspLoading(true); setInspErr(null);
    try {
      const res  = await fetch(INSPECTION_URL, { method: 'POST', body: JSON.stringify({ action: 'inspectionRegister' }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'unknown error');
      setInspections(data.inspections || []); setInspLoaded(true);
    } catch (err) { setInspErr(err.message); }
    finally { setInspLoading(false); }
  }, []);
  useEffect(() => {
    if (tab === 'inspections' && !inspLoaded && !inspLoading) loadInspections();
  }, [tab, inspLoaded, inspLoading, loadInspections]);


  const inspTodayISO = todayISO();
  // Service standing for a check: overdue (past km or date), due soon (≤500km / ≤30d), else ok.
  // The next-service km is the canonical per-rego value (latest service + interval), NOT the
  // figure snapshotted onto the van check at scan time — so a check saved while the old stale
  // milestone was live still reads correctly once the schedule is fixed. Falls back to the
  // snapshot only for a rego with no derivable milestone (e.g. a van not in the fleet list).
  const inspSvc = useCallback((r) => {
    const nextKm = nextKmByRego[r.rego] || r.nextServiceKm || 0;
    const kmLeft = (nextKm && r.odometer != null) ? nextKm - r.odometer : null;
    const dDays  = r.nextServiceDate ? Math.ceil((new Date(r.nextServiceDate) - new Date(inspTodayISO)) / 86400000) : null;
    const overdue = (kmLeft != null && kmLeft < 0) || (dDays != null && dDays < 0);
    const soon = !overdue && ((kmLeft != null && kmLeft >= 0 && kmLeft <= 500) || (dDays != null && dDays >= 0 && dDays <= 30));
    return { kmLeft, nextKm, dDays, status: overdue ? 'overdue' : soon ? 'soon' : 'ok' };
  }, [inspTodayISO, nextKmByRego]);

  const inspDrivers = useMemo(() => [...new Set(inspections.map(r => r.driver).filter(Boolean))].sort(), [inspections]);
  const inspRegos   = useMemo(() => [...new Set(inspections.map(r => r.rego).filter(Boolean))].sort(), [inspections]);

  const inspRows = useMemo(() => {
    const rows = inspections.filter(r => {
      if (inspDriver && r.driver !== inspDriver) return false;
      if (inspRego && r.rego !== inspRego) return false;
      if (inspFrom && r.inspectionDate < inspFrom) return false;
      if (inspTo && r.inspectionDate > inspTo) return false;
      if (inspFaults && !(r.faultCount > 0)) return false;
      if (inspStatusF && inspSvc(r).status !== inspStatusF) return false;
      return true;
    });
    const dir = inspSort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const f = inspSort.field;
      let av, bv;
      if (f === 'kmLeft')        { av = inspSvc(a).kmLeft ?? Infinity; bv = inspSvc(b).kmLeft ?? Infinity; }
      else if (f === 'odometer') { av = a.odometer ?? -1; bv = b.odometer ?? -1; }
      else if (f === 'nextServiceKm') { av = inspSvc(a).nextKm ?? -1; bv = inspSvc(b).nextKm ?? -1; }
      else { av = a[f] || ''; bv = b[f] || ''; }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [inspections, inspDriver, inspRego, inspFrom, inspTo, inspFaults, inspStatusF, inspSort, inspSvc]);

  const toggleInspSort = (field) => setInspSort(s => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  const inspClearFilters = () => { setInspDriver(''); setInspRego(''); setInspFrom(''); setInspTo(''); setInspStatusF(''); setInspFaults(false); };
  const inspFiltered = inspDriver || inspRego || inspFrom || inspTo || inspStatusF || inspFaults;

  // ── Persistent issues (open until resolved with a remedy) ──
  const loadIssues = useCallback(async () => {
    try {
      const res  = await fetch(INSPECTION_URL, { method: 'POST', body: JSON.stringify({ action: 'issuesRegister' }) });
      const data = await res.json();
      setIssues(data.success ? (data.issues || []) : []); setIssuesLoaded(true);
    } catch { setIssues([]); setIssuesLoaded(true); }
  }, []);
  useEffect(() => { if (tab === 'inspections' && !issuesLoaded) loadIssues(); }, [tab, issuesLoaded, loadIssues]);

  const resolveIssue = async (id, remedy, reopen = false) => {
    setIssues(prev => (prev || []).map(i => i.id === id
      ? { ...i, status: reopen ? 'open' : 'resolved', remedy: reopen ? '' : remedy, resolvedDate: reopen ? null : todayISO() }
      : i));
    setResolveId(null); setRemedyText('');
    try {
      await fetch(INSPECTION_URL, { method: 'POST', body: JSON.stringify({ action: 'issueResolve', id, remedy, reopen }) });
      if (refreshInspectionAlerts) refreshInspectionAlerts();
    } catch { addNotification('Could not update — try again', 'error'); loadIssues(); }
  };
  const openIssuesList = (issues || []).filter(i => i.status === 'open');
  const resolvedIssuesList = (issues || []).filter(i => i.status === 'resolved');

  const inspCsvCell = (v) => { const s = (v == null ? '' : String(v)); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const downloadInspCSV = () => {
    const header = ['Date', 'Driver login', 'Driver name', 'Rego', 'Odometer', 'Service due (km)', 'Km to service', 'Service due (date)', 'Days to service', 'Service status', 'Faults', 'Fault details'];
    const lines = inspRows.map(r => {
      const s = inspSvc(r); const drv = getDriver(r.driver);
      const faultDetail = (r.items || []).filter(i => i.status === 'fault').map(i => i.name + (i.note ? ` (${i.note})` : '')).join('; ');
      return [r.inspectionDate, r.driver, drv?.name || '', r.rego, r.odometer ?? '', s.nextKm || '', s.kmLeft ?? '', r.nextServiceDate || '', s.dDays ?? '', s.status, r.faultCount || 0, faultDetail];
    });
    const csv = [header, ...lines].map(row => row.map(inspCsvCell).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `vehicle-inspections-${todayISO()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openInspDetail = async (r) => {
    setInspDetail({ row: r, full: null, loading: true });
    try {
      const res  = await fetch(INSPECTION_URL, { method: 'POST', body: JSON.stringify({ action: 'inspectionGet', id: r.id }) });
      const data = await res.json();
      setInspDetail({ row: r, full: data.success ? data.inspection : null, loading: false });
    } catch { setInspDetail({ row: r, full: null, loading: false }); }
  };

  // ── Mutations ──
  const openLogDrawer = (rego = '', status = 'Booked') => {
    setDrawer({
      ...EMPTY_RECORD, rego, status,
      bookedDate: status === 'Booked' ? '' : todayISO(),
      dateIn: status === 'In Workshop' ? todayISO() : '',
    });
  };

  const handleSaved = (rec) => {
    setRecords(prev => {
      const exists = prev.find(r => r.id === rec.id);
      return exists ? prev.map(r => r.id === rec.id ? { ...r, ...rec } : r) : [...prev, rec];
    });
    // Mirror the server-side Fleet service_due write into local state.
    if (rec.status === 'Completed' && rec.nextServiceDate) {
      setVehicles(vs => vs.map(v => v.rego === rec.rego ? { ...v, service_due: rec.nextServiceDate } : v));
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm(`Delete this ${rec.type || 'record'} for ${rec.rego}? This removes the row from Google Sheets.`)) return;
    setDeleting(prev => new Set([...prev, rec.id]));
    try {
      const res  = await fetch(VEHICLE_SERVICE_URL, { method: 'POST', body: JSON.stringify({ action: 'deleteVehicleService', id: rec.id }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'unknown error');
      setRecords(prev => prev.filter(r => r.id !== rec.id));
      addNotification(`${rec.rego} record deleted`, 'success');
    } catch (err) {
      addNotification(`Delete failed: ${err.message}`, 'error');
    } finally {
      setDeleting(prev => { const n = new Set(prev); n.delete(rec.id); return n; });
    }
  };

  const viewHistory = (rego) => {
    setSearch(rego);
    setTypeFilter('');
    setStatusFilter('');
    setTab('history');
    setSearchParams({ rego }, { replace: true });
  };

  const TABS = [
    ['schedule',    'Service Schedule'],
    ['history',     'Repairs & History'],
    ['costs',       'Costs & Downtime'],
    ['inspections', 'Inspections'],
  ];

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Service &amp; Repairs</h2>
          <p>Fleet maintenance, repair history, costs &amp; downtime — preferred workshop: {DEFAULT_PROVIDER}</p>
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {TABS.map(([key, label]) => (
              <button key={key} className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={loadRecords} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-secondary" onClick={() => openLogDrawer()} title="Drop a workshop invoice — the record fills itself">
            <FileText size={14} /> Upload Invoice
          </button>
          <button className="btn btn-primary" onClick={() => openLogDrawer()}>
            <Plus size={14} /> Log Service / Repair
          </button>
        </div>
      </div>

      <div className="page-body">

        {/* ── Stats ── */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 24 }}>
          {[
            { label: 'Off Road Now',            value: stats.offRoad,  accent: stats.offRoad > 0 ? 'var(--hp-red)' : 'var(--hp-green)' },
            { label: 'Overdue Service',         value: stats.overdue,  accent: stats.overdue > 0 ? 'var(--hp-red)' : 'var(--hp-green)' },
            { label: 'Due ≤ 30 Days',           value: stats.dueSoon,  accent: stats.dueSoon > 0 ? 'var(--hp-amber)' : 'var(--hp-green)' },
            { label: `${fyLabel()} Cost (ex GST)`, value: fmtMoney(stats.fyCost), accent: 'var(--hp-gold)' },
            { label: `${fyLabel()} Downtime`,   value: `${stats.fyDowntime} day${stats.fyDowntime === 1 ? '' : 's'}`, accent: 'var(--hp-blue)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: typeof s.value === 'number' ? '2rem' : '1.4rem' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {loadError && (
          <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-red)55', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle size={15} color="var(--hp-red)" />
            <span style={{ fontSize: '0.85rem' }}>Could not load service records: {loadError}</span>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={loadRecords}>Retry</button>
          </div>
        )}

        {inspectionAlertCount > 0 && tab !== 'inspections' && (
          <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-red)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <AlertTriangle size={16} color="var(--hp-red)" />
            <span style={{ fontSize: '0.85rem' }}>
              <strong>{inspectionAlertCount}</strong> morning van check issue{inspectionAlertCount > 1 ? 's' : ''} flagged by drivers need attention.
            </span>
            <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setTab('inspections')}>
              Review issues
            </button>
          </div>
        )}

        <ServiceAlertPanel summaries={scopedSummaries} watchAlerts={watchAlerts} onWatch={setWatchRego} onLog={(rego, status) => openLogDrawer(rego, status)} onBackOnRoad={setBackOnRoad} />

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '30px 0', color: 'var(--hp-muted)', fontSize: '0.85rem' }}>
            <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading service records…
          </div>
        )}

        {/* ── Search (Schedule + Costs tabs; History + Inspections have their own filter bars) ── */}
        {!loading && tab !== 'history' && tab !== 'inspections' && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '0 0 300px' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-muted)', pointerEvents: 'none' }} />
              <input
                className="input"
                style={{ paddingLeft: 32, background: 'var(--hp-surface)', color: 'var(--hp-text)', width: '100%' }}
                placeholder="Search rego, driver, login or invoice no…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="input" title="Filter by vehicle owner"
              style={{ background: 'var(--hp-surface)', color: ownerF ? 'var(--hp-gold)' : 'var(--hp-text)', width: 'auto', fontSize: '0.82rem' }}
              value={ownerF} onChange={e => setOwnerF(e.target.value)}
            >
              <option value="">All owners</option>
              <option value="OURS">Ours to service — PHB · HPL · SG Fleet</option>
              {owners.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select
              className="input" title="Filter by service standing"
              style={{ background: 'var(--hp-surface)', color: dueF ? 'var(--hp-gold)' : 'var(--hp-text)', width: 'auto', fontSize: '0.82rem' }}
              value={dueF} onChange={e => setDueF(e.target.value)}
            >
              <option value="">Any standing</option>
              <option value="overdue">Overdue</option>
              <option value="soon">Due soon (≤ 30 days / ≤ 500 km)</option>
              <option value="ok">On track</option>
              <option value="open">In workshop / booked</option>
              <option value="none">No due target</option>
              <option value="reminder">Has a reminder</option>
            </select>
            {(search.trim() || ownerF || dueF) && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setOwnerF(''); setDueF(''); setSearchParams({}, { replace: true }); }}>
                  <X size={12} /> Clear
                </button>
                <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)', marginLeft: 'auto' }}>
                  {filteredSummaries.length} vehicle{filteredSummaries.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
        )}

        {/* ── Schedule tab ── */}
        {!loading && tab === 'schedule' && (
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Driver</th>
                  <th>Last Service</th>
                  <th>Odometer</th>
                  <th>Next Due</th>
                  <th>Status</th>
                  <th>{fyLabel()} Cost</th>
                  <th>Downtime</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSummaries.map(s => {
                  const v = s.vehicle;
                  const driver = getDriver(v.login);
                  return (
                    <tr key={s.rego}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, background: 'var(--hp-surface)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Truck size={14} color="var(--hp-dim)" />
                          </div>
                          <div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{s.rego}</div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)' }}>
                              {vehicleLabel(s.rego) || 'Details pending'}
                              {(v.owner || '').trim() && <span style={{ color: 'var(--hp-dim)' }}> · {v.owner.trim()}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {driver
                          ? <div><div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{driver.name}</div><div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{driver.loginName}</div></div>
                          : <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>{v.login || '—'}</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {s.lastService
                          ? <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {fmtDate(recordDate(s.lastService)) || '—'}
                                {s.lastService.invoicePdfUrl && (
                                  <a href={s.lastService.invoicePdfUrl} target="_blank" rel="noopener noreferrer"
                                    title="Open service invoice PDF" style={{ display: 'inline-flex', color: 'var(--hp-gold)' }}>
                                    <FileText size={12} />
                                  </a>
                                )}
                              </div>
                              {s.lastService.odometer > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)' }}>{fmtKm(s.lastService.odometer)}</div>}
                            </>
                          : <span style={{ color: 'var(--hp-muted)' }}>No record</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {s.currentOdo
                          ? <>
                              <div>{fmtKm(s.currentOdo.odometer)}</div>
                              {s.kmLeft != null && (
                                <div style={{ fontSize: '0.68rem', fontWeight: 600, color: s.kmLeft < 0 ? 'var(--hp-red)' : 'var(--hp-muted)' }}>
                                  {s.kmLeft >= 0 ? `${fmtKm(s.kmLeft)} to go` : `${fmtKm(Math.abs(s.kmLeft))} over`}
                                </div>
                              )}
                              {s.currentOdo.date && <div style={{ fontSize: '0.66rem', color: 'var(--hp-dim)' }}>as at {fmtDate(s.currentOdo.date)}</div>}
                            </>
                          : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {s.nextDue
                          ? <>
                              <div>{fmtDate(s.nextDue)}</div>
                              {s.nextDueKm > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)' }}>or {fmtKm(s.nextDueKm)}</div>}
                            </>
                          : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                      </td>
                      <td>
                        {s.openRecord
                          ? <StatusPill status={s.openRecord.status} />
                          : <DuePill days={s.dueDays} hasDate={!!s.nextDue} kmLeft={s.kmLeft} />}
                        {(openWatchesByRego[s.rego] || []).length > 0 && (
                          <div>{openWatchesByRego[s.rego].map(({ w, st }) => <WatchChip key={w.id} w={w} st={st} onClick={() => setWatchRego(s.rego)} />)}</div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{s.fyCost > 0 ? fmtMoney(s.fyCost) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{s.fyDowntime > 0 ? `${s.fyDowntime}d` : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {s.openRecord?.status === 'In Workshop' && (
                            <button className="btn btn-ghost btn-sm" title="Close this job — the van drops off the alerts" onClick={() => setBackOnRoad(s.openRecord)}>
                              <CheckCircle size={12} /> Back on road
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm" title="Log service / repair" onClick={() => openLogDrawer(s.rego)}>
                            <Wrench size={12} /> Log
                          </button>
                          <button className="btn btn-ghost btn-sm" title="Set a maintenance reminder — e.g. brakes in 5,000–10,000 km. The driver is told too." onClick={() => setWatchRego(s.rego)}>
                            <Bell size={12} /> Remind
                          </button>
                          <button className="btn btn-ghost btn-sm" title="View history" onClick={() => viewHistory(s.rego)} disabled={s.jobCount === 0}>
                            <History size={12} /> {s.jobCount}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredSummaries.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--hp-muted)', fontSize: '0.85rem' }}>
                    {search.trim() ? `No vehicles match "${search.trim()}"` : 'No vehicles in the fleet yet — add them on the Fleet page'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── History tab ── */}
        {!loading && tab === 'history' && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '0 0 280px' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-muted)', pointerEvents: 'none' }} />
                <input className="input" style={{ paddingLeft: 32, background: 'var(--hp-surface)', color: 'var(--hp-text)', width: '100%' }}
                  placeholder="Search rego, driver, workshop, invoice…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className="input" style={{ background: 'var(--hp-surface)', color: typeFilter ? 'var(--hp-text)' : 'var(--hp-muted)', fontSize: '0.8rem', padding: '0 10px', height: 34, minWidth: 160 }}
                value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="input" style={{ background: 'var(--hp-surface)', color: statusFilter ? 'var(--hp-text)' : 'var(--hp-muted)', fontSize: '0.8rem', padding: '0 10px', height: 34, minWidth: 140 }}
                value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {(search || typeFilter || statusFilter) && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter(''); setSearchParams({}, { replace: true }); }}>
                  <X size={12} /> Clear
                </button>
              )}
              {retiredRecordCount > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--hp-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} />
                  Show retired vans ({retiredRecordCount})
                </label>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)', marginLeft: 'auto' }}>
                {historyRows.length} record{historyRows.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vehicle</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Work / Workshop</th>
                    <th>Odometer</th>
                    <th>Cost (ex GST)</th>
                    <th>Downtime</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        <div>{fmtDate(recordDate(r)) || '—'}</div>
                        {r.status === 'Completed' && r.dateOut && r.dateIn && r.dateOut !== r.dateIn && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>out {fmtDate(r.dateOut)}</div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{r.rego}</span>
                        {retiredByRego[r.rego] && (
                          <span title={`Retired ${fmtDate(retiredByRego[r.rego].archivedAt) || ''}${retiredByRego[r.rego].archivedReason ? ` — ${retiredByRego[r.rego].archivedReason}` : ''}`}
                            style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 12, background: 'var(--hp-surface)',
                              border: '1px solid var(--hp-border)', color: 'var(--hp-muted)', fontWeight: 700,
                              fontSize: '0.62rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Retired
                          </span>
                        )}
                        <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>{vehicleLabel(r.rego)}</div>
                      </td>
                      <td><TypeBadge type={r.type} /></td>
                      <td><StatusPill status={r.status} /></td>
                      <td style={{ maxWidth: 280 }}>
                        <div style={{ fontSize: '0.82rem' }}>{r.description || <span style={{ color: 'var(--hp-muted)' }}>—</span>}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{r.provider || '—'}{r.invoiceNo ? ` · Inv ${r.invoiceNo}` : ''}</span>
                          {r.visibility === 'operator' && (
                            <span title="Visible to the van's current driver"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12,
                                background: 'rgba(34,197,94,0.12)', border: '1px solid var(--hp-green)55', color: 'var(--hp-green)',
                                fontWeight: 700, fontSize: '0.66rem', letterSpacing: '0.03em' }}>
                              <Eye size={10} /> Driver-visible
                            </span>
                          )}
                          {r.invoicePdfUrl && (
                            <>
                              <a href={r.invoicePdfUrl} target="_blank" rel="noopener noreferrer"
                                title="View invoice PDF"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 8px', borderRadius: 12, textDecoration: 'none',
                                  background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)55',
                                  color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.68rem',
                                  fontFamily: 'var(--font-mono)', letterSpacing: '0.04em',
                                }}>
                                <FileText size={10} /> PDF
                              </a>
                              {pdfDownloadUrl(r) && (
                                <a href={pdfDownloadUrl(r)}
                                  title="Download invoice PDF"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '2px 8px', borderRadius: 12, textDecoration: 'none',
                                    background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                                    color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.68rem',
                                    fontFamily: 'var(--font-mono)', letterSpacing: '0.04em',
                                  }}>
                                  <Download size={10} />
                                </a>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{r.odometer > 0 ? fmtKm(r.odometer) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{r.cost > 0 ? fmtMoney(r.cost) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {r.status === 'In Workshop' && !retiredByRego[r.rego]
                          ? <span style={{ color: 'var(--hp-red)', fontWeight: 600 }}>{liveDowntime(r)}d so far</span>
                          : r.status === 'In Workshop'
                          ? <span style={{ color: 'var(--hp-muted)' }}>van retired</span>
                          : r.downtimeDays > 0 ? `${r.downtimeDays}d` : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setDrawer({ ...r })}>
                            <Pencil size={12} />
                          </button>
                          <button className="btn btn-ghost btn-sm" title="Delete" disabled={deleting.has(r.id)} onClick={() => handleDelete(r)}>
                            {deleting.has(r.id) ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={12} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {historyRows.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--hp-muted)', fontSize: '0.85rem' }}>
                      No service or repair records {search || typeFilter || statusFilter ? 'match these filters' : 'yet — use "Log Service / Repair" to add the first one'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Costs & downtime tab ── */}
        {!loading && tab === 'costs' && (
          <>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              {/* Monthly spend */}
              <div className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <DollarSign size={15} color="var(--hp-gold)" />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Monthly Spend — last 12 months (ex GST)</span>
                </div>
                {costByMonth.map(m => (
                  <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-muted)', width: 56, flexShrink: 0 }}>{m.label}</span>
                    <div style={{ flex: 1, height: 14, background: 'var(--hp-surface)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.round((m.cost / maxMonthCost) * 100)}%`, background: 'var(--hp-gold)', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', width: 90, textAlign: 'right', color: m.cost > 0 ? 'var(--hp-text)' : 'var(--hp-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {m.cost > 0 ? fmtMoney(m.cost) : '—'}
                    </span>
                  </div>
                ))}
              </div>

              {/* By type, this FY */}
              <div className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <Wrench size={15} color="var(--hp-gold)" />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Spend by Job Type — {fyLabel()} (ex GST)</span>
                </div>
                {costByType.length === 0 && <div style={{ color: 'var(--hp-muted)', fontSize: '0.82rem' }}>No costs recorded this financial year</div>}
                {costByType.map(t => (
                  <div key={t.type} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLORS[t.type] || 'var(--hp-dim)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.82rem', flex: 1 }}>{t.type}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--hp-muted)' }}>{t.jobs} job{t.jobs !== 1 ? 's' : ''}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, width: 100, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(t.cost)}</span>
                  </div>
                ))}
                {costByType.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--hp-border)', paddingTop: 10, marginTop: 4 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>Total {fyLabel()}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-gold)' }}>{fmtMoney(stats.fyCost)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Spend by workshop */}
            <div className="card" style={{ padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Truck size={15} color="var(--hp-gold)" />
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Spend by Workshop (ex GST)</span>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: '0.75rem', color: 'var(--hp-muted)' }}>
                Every business you've spent with, all-time — bar shows share of total spend
              </p>
              {costByProvider.length === 0 && <div style={{ color: 'var(--hp-muted)', fontSize: '0.82rem' }}>No costs recorded yet</div>}
              {costByProvider.map((p, i) => (
                <div key={p.provider} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--hp-muted)', width: 18, flexShrink: 0 }}>{i + 1}</span>
                  <div style={{ flex: '0 0 280px', minWidth: 0 }}>
                    <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--hp-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.provider}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>
                      {p.jobs} job{p.jobs !== 1 ? 's' : ''} all-time{p.fyJobs > 0 ? ` · ${p.fyJobs} this FY` : ''}
                    </div>
                  </div>
                  <div style={{ flex: 1, height: 14, background: 'var(--hp-surface)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.max(2, Math.round((p.cost / providerTotal) * 100))}%`, background: i === 0 ? 'var(--hp-gold)' : 'var(--hp-blue)', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', width: 42, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round((p.cost / providerTotal) * 100)}%
                  </span>
                  <div style={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.cost)}</div>
                    {p.fyCost > 0 && p.fyCost !== p.cost && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.fyCost)} this FY</div>
                    )}
                  </div>
                </div>
              ))}
              {costByProvider.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--hp-border)', paddingTop: 10, marginTop: 4 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>{costByProvider.length} workshop{costByProvider.length !== 1 ? 's' : ''} · all-time total</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-gold)' }}>{fmtMoney(costByProvider.reduce((s, p) => s + p.cost, 0))}</span>
                </div>
              )}
            </div>

            {/* Per-vehicle table */}
            <div className="card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Jobs</th>
                    <th>{fyLabel()} Cost</th>
                    <th>All-Time Cost</th>
                    <th>{fyLabel()} Downtime</th>
                    <th>All-Time Downtime</th>
                    <th>Avg Cost / Job</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filteredSummaries].sort((a, b) => b.fyCost - a.fyCost).map(s => (
                    <tr key={s.rego}>
                      <td>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{s.rego}</span>
                        <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>{vehicleLabel(s.rego)}</div>
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>{s.jobCount || <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{s.fyCost > 0 ? fmtMoney(s.fyCost) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{s.allCost > 0 ? fmtMoney(s.allCost) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem' }}>{s.fyDowntime > 0 ? `${s.fyDowntime}d` : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem' }}>{s.allDowntime > 0 ? `${s.allDowntime}d` : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{s.jobCount > 0 && s.allCost > 0 ? fmtMoney(s.allCost / s.jobCount) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16, fontSize: '0.75rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={12} />
              Downtime = days off road logged per job; vehicles currently in the workshop accrue live days until the job is completed.
            </div>
          </>
        )}

        {/* ── Inspections register tab ── */}
        {!loading && tab === 'inspections' && (
          <>
            {/* ── Open issues panel — persistent until resolved with a remedy ── */}
            <div className="card" style={{ padding: 18, marginBottom: 20, borderColor: openIssuesList.length ? 'var(--hp-red)' : 'var(--hp-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: openIssuesList.length ? 14 : 0 }}>
                <AlertTriangle size={15} color={openIssuesList.length ? 'var(--hp-red)' : 'var(--hp-green)'} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Open issues{openIssuesList.length ? ` (${openIssuesList.length})` : ''}</span>
                {!openIssuesList.length && <span style={{ fontSize: '0.8rem', color: 'var(--hp-muted)' }}>— none open, all resolved.</span>}
                {resolvedIssuesList.length > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowResolved(v => !v)}>
                    {showResolved ? 'Hide' : 'Show'} resolved ({resolvedIssuesList.length})
                  </button>
                )}
              </div>

              {openIssuesList.map(iss => {
                const daysOpen = (() => { try { return Math.floor((new Date(inspTodayISO) - new Date(iss.reportedDate)) / 86400000); } catch { return null; } })();
                return (
                  <div key={iss.id} style={{ padding: '11px 13px', borderRadius: 10, border: '1px solid var(--hp-red)55', background: 'var(--hp-red)0d', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.86rem', fontWeight: 700 }}>
                          {iss.itemName}
                          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)', fontWeight: 800, marginLeft: 8 }}>{iss.rego}</span>
                        </div>
                        {iss.problem && <div style={{ fontSize: '0.78rem', color: 'var(--hp-text)' }}>{iss.problem}</div>}
                        <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginTop: 2 }}>
                          Reported {iss.reportedBy ? `by ${getDriver(iss.reportedBy)?.name || iss.reportedBy} ` : ''}
                          {(() => { try { return new Date(iss.reportedDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return iss.reportedDate; } })()}
                          {daysOpen != null && daysOpen > 0 ? ` · ${daysOpen} day${daysOpen > 1 ? 's' : ''} open` : ''}
                        </div>
                      </div>
                      {iss.photoUrl && <a href={iss.photoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><Camera size={13} /> Photo</a>}
                      {resolveId !== iss.id && (
                        <button className="btn btn-primary btn-sm" onClick={() => { setResolveId(iss.id); setRemedyText(''); }}><CheckCircle size={13} /> Resolve</button>
                      )}
                    </div>
                    {resolveId === iss.id && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <input className="input" autoFocus value={remedyText} onChange={e => setRemedyText(e.target.value)} placeholder="Remedy — e.g. new tyre fitted, booked in for service"
                          style={{ flex: 1, background: 'var(--hp-surface)', color: 'var(--hp-text)' }} />
                        <button className="btn btn-secondary btn-sm" onClick={() => { setResolveId(null); setRemedyText(''); }}>Cancel</button>
                        <button className="btn btn-primary btn-sm" disabled={!remedyText.trim()} onClick={() => resolveIssue(iss.id, remedyText.trim())}>Save</button>
                      </div>
                    )}
                  </div>
                );
              })}

              {showResolved && resolvedIssuesList.map(iss => (
                <div key={iss.id} style={{ padding: '9px 13px', borderRadius: 10, border: '1px solid var(--hp-border)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CheckCircle size={13} color="var(--hp-green)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: 600 }}>{iss.itemName}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)', fontWeight: 700, marginLeft: 6 }}>{iss.rego}</span>
                    {iss.remedy && <span style={{ color: 'var(--hp-muted)' }}> — {iss.remedy}</span>}
                    <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)' }}>
                      Resolved {iss.resolvedDate ? (() => { try { return new Date(iss.resolvedDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iss.resolvedDate; } })() : ''}{iss.resolvedBy ? ` by ${getDriver(iss.resolvedBy)?.name || iss.resolvedBy}` : ''}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => resolveIssue(iss.id, '', true)}>Reopen</button>
                </div>
              ))}
            </div>


            {/* filter bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
              <div>
                <label style={INSP_FLAB}>Driver</label>
                <select className="input" value={inspDriver} onChange={e => setInspDriver(e.target.value)} style={{ minWidth: 150 }}>
                  <option value="">All drivers</option>
                  {inspDrivers.map(d => <option key={d} value={d}>{getDriver(d)?.name || d}</option>)}
                </select>
              </div>
              <div>
                <label style={INSP_FLAB}>Rego</label>
                <select className="input" value={inspRego} onChange={e => setInspRego(e.target.value)} style={{ minWidth: 110 }}>
                  <option value="">All</option>
                  {inspRegos.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={INSP_FLAB}>From</label>
                <input type="date" className="input" value={inspFrom} onChange={e => setInspFrom(e.target.value)} />
              </div>
              <div>
                <label style={INSP_FLAB}>To</label>
                <input type="date" className="input" value={inspTo} onChange={e => setInspTo(e.target.value)} />
              </div>
              <div>
                <label style={INSP_FLAB}>Service standing</label>
                <select className="input" value={inspStatusF} onChange={e => setInspStatusF(e.target.value)} style={{ minWidth: 130 }}>
                  <option value="">Any</option>
                  <option value="overdue">Overdue</option>
                  <option value="soon">Due soon</option>
                  <option value="ok">OK</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--hp-text)', paddingBottom: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={inspFaults} onChange={e => setInspFaults(e.target.checked)} /> Faults only
              </label>
              {inspFiltered && (
                <button className="btn btn-ghost btn-sm" onClick={inspClearFilters} style={{ marginBottom: 4 }}><X size={12} /> Clear</button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)' }}>{inspRows.length} check{inspRows.length !== 1 ? 's' : ''}</span>
                <button className="btn btn-secondary btn-sm" onClick={loadInspections} disabled={inspLoading}><RefreshCw size={13} /> Refresh</button>
                <button className="btn btn-primary btn-sm" onClick={downloadInspCSV} disabled={!inspRows.length}><Download size={13} /> Download CSV</button>
              </div>
            </div>

            {inspErr && (
              <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-red)55', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={15} color="var(--hp-red)" />
                <span style={{ fontSize: '0.85rem' }}>Could not load inspections: {inspErr}</span>
                <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={loadInspections}>Retry</button>
              </div>
            )}
            {inspLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '30px 0', color: 'var(--hp-muted)', fontSize: '0.85rem' }}>
                <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading inspection register…
              </div>
            )}

            {!inspLoading && inspLoaded && (
              <div className="card">
                <table className="data-table">
                  <thead>
                    <tr>
                      {[['inspectionDate', 'Date'], ['driver', 'Driver'], ['rego', 'Rego'], ['odometer', 'Odometer'], ['nextServiceKm', 'Service due (km)'], ['nextServiceDate', 'Service due (date)']].map(([f, label]) => (
                        <th key={f} onClick={() => toggleInspSort(f)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
                          {label}{inspSort.field === f ? (inspSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>
                      ))}
                      <th>Standing</th>
                      <th>Faults</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspRows.length === 0 && (
                      <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--hp-muted)', padding: '24px 0', fontSize: '0.85rem' }}>
                        {inspections.length === 0 ? 'No inspections recorded yet.' : 'No inspections match these filters.'}
                      </td></tr>
                    )}
                    {inspRows.map(r => {
                      const s = inspSvc(r); const drv = getDriver(r.driver);
                      const pill = s.status === 'overdue' ? { bg: 'var(--hp-red)', label: 'Overdue' } : s.status === 'soon' ? { bg: 'var(--hp-amber)', label: 'Due soon' } : { bg: 'var(--hp-green)', label: 'OK' };
                      return (
                        <tr key={r.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: '0.82rem' }}>{new Date(r.inspectionDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)' }}>{r.inspectedAt ? new Date(r.inspectedAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : ''}</div>
                          </td>
                          <td style={{ fontSize: '0.82rem' }}>
                            {drv?.name || r.driver}
                            <div style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)' }}>{r.driver}</div>
                          </td>
                          <td><span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{r.rego || '—'}</span></td>
                          <td style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{r.odometer != null ? r.odometer.toLocaleString('en-AU') + ' km' : <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                          <td style={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                            {s.nextKm ? s.nextKm.toLocaleString('en-AU') + ' km' : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                            {s.kmLeft != null && <div style={{ fontSize: '0.68rem', color: s.kmLeft < 0 ? 'var(--hp-red)' : 'var(--hp-muted)' }}>{s.kmLeft >= 0 ? `${s.kmLeft.toLocaleString('en-AU')} km to go` : `${Math.abs(s.kmLeft).toLocaleString('en-AU')} km over`}</div>}
                          </td>
                          <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {r.nextServiceDate ? new Date(r.nextServiceDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                            {s.dDays != null && <div style={{ fontSize: '0.68rem', color: s.dDays < 0 ? 'var(--hp-red)' : 'var(--hp-muted)' }}>{s.dDays >= 0 ? `in ${s.dDays}d` : `${Math.abs(s.dDays)}d over`}</div>}
                          </td>
                          <td>
                            <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, color: '#fff', background: pill.bg }}>{pill.label}</span>
                          </td>
                          <td>
                            {r.faultCount > 0
                              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.82rem' }}><AlertTriangle size={13} /> {r.faultCount}</span>
                              : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--hp-green)', fontSize: '0.82rem' }}><CheckCircle size={13} /> Clear</span>}
                          </td>
                          <td>
                            <button className="btn btn-ghost btn-sm" onClick={() => openInspDetail(r)} title="View check detail"><Eye size={13} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 16, fontSize: '0.75rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ClipboardCheck size={12} />
              Each row is a driver's morning van check. Service standing is derived from the odometer and next-service targets recorded on the check. Open a row to see the item-by-item result and any fault photos.
            </div>
          </>
        )}
      </div>

      {drawer && (
        <RecordDrawer
          record={drawer} vehicles={vehicles} personnel={personnel} providers={providers}
          onClose={() => setDrawer(null)} onSaved={handleSaved} addNotification={addNotification}
        />
      )}

      {watchRego && (() => {
        const v = vehicles.find(x => x.rego === watchRego);
        const sum = summaries.find(x => x.rego === watchRego);
        return (
          <WatchModal
            rego={watchRego} vehicleText={vehicleLabel(watchRego)} driver={(v?.login || '').toUpperCase()}
            odo={latestOdos[watchRego] || null} lastService={sum?.lastService || null}
            watches={watches.filter(w => w.rego === watchRego)}
            onClose={() => setWatchRego(null)} onChanged={upsertWatch} addNotification={addNotification}
          />
        );
      })()}

      {backOnRoad && (
        <BackOnRoadModal
          record={backOnRoad}
          driverName={getDriver(vehicles.find(v => v.rego === backOnRoad.rego)?.login)?.name || ''}
          onClose={() => setBackOnRoad(null)}
          onDone={(saved) => { handleSaved(saved); setBackOnRoad(null); }}
          addNotification={addNotification}
        />
      )}

      {inspDetail && (
        <div onClick={() => setInspDetail(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 540, width: '100%', padding: 22, marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ClipboardCheck size={16} color="var(--hp-gold)" />
                  {getDriver(inspDetail.row.driver)?.name || inspDetail.row.driver}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-muted)', marginTop: 3 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)', fontWeight: 700 }}>{inspDetail.row.rego || '—'}</span>
                  {' · '}{new Date(inspDetail.row.inspectionDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  {inspDetail.row.inspectedAt ? ` · ${new Date(inspDetail.row.inspectedAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}` : ''}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setInspDetail(null)}><X size={14} /></button>
            </div>

            <div style={{ display: 'flex', gap: 20, marginBottom: 16, fontSize: '0.82rem' }}>
              <div><div style={INSP_FLAB}>Odometer</div><div style={{ fontWeight: 700 }}>{inspDetail.row.odometer != null ? inspDetail.row.odometer.toLocaleString('en-AU') + ' km' : '—'}</div></div>
              <div><div style={INSP_FLAB}>Service due (km)</div><div style={{ fontWeight: 700 }}>{inspSvc(inspDetail.row).nextKm ? inspSvc(inspDetail.row).nextKm.toLocaleString('en-AU') + ' km' : '—'}</div></div>
              <div><div style={INSP_FLAB}>Service due (date)</div><div style={{ fontWeight: 700 }}>{inspDetail.row.nextServiceDate ? new Date(inspDetail.row.nextServiceDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</div></div>
            </div>

            {inspDetail.loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-muted)', fontSize: '0.82rem', padding: '10px 0' }}>
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading check…
              </div>
            )}
            {!inspDetail.loading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {((inspDetail.full && inspDetail.full.items) || inspDetail.row.items || []).map((it, i) => {
                  const fault = it.status === 'fault';
                  return (
                    <div key={it.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 8, border: `1px solid ${fault ? 'var(--hp-red)55' : 'var(--hp-border)'}`, background: fault ? 'var(--hp-red)11' : 'transparent' }}>
                      {fault ? <AlertTriangle size={14} color="var(--hp-red)" style={{ flexShrink: 0 }} /> : <CheckCircle size={14} color="var(--hp-green)" style={{ flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.84rem', fontWeight: fault ? 700 : 500 }}>{it.name}</div>
                        {fault && it.note && <div style={{ fontSize: '0.74rem', color: 'var(--hp-red)' }}>{it.note}</div>}
                      </div>
                      {fault && it.photoUrl && (
                        <a href={it.photoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><Camera size={13} /> Photo</a>
                      )}
                      {fault && !it.photoUrl && (it.hasPhoto || (inspDetail.full == null)) && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--hp-muted)' }}>photo…</span>
                      )}
                    </div>
                  );
                })}
                {(((inspDetail.full && inspDetail.full.items) || inspDetail.row.items || []).length === 0) && (
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-muted)' }}>No item detail recorded.</div>
                )}
              </div>
            )}

            {inspDetail.row.faultCount > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--hp-border)', fontSize: '0.75rem', color: 'var(--hp-muted)' }}>
                Faults here open a tracked issue on the van — resolve them in the <strong>Open issues</strong> panel at the top of the Inspections tab.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
